import React, { useEffect, useState, useRef } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowRight, Gift, Eye, EyeOff } from "lucide-react";
import { getErrorMessage } from "@/lib/api";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
const LOGO_SRC = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAIAAAACACAYAAADDPmHLAABjuElEQVR4nN29d7xeRbU+/qyZvd92WnqvhEACoYdeEpAi0pGEoCAKXtGrKGK/yE0iolz02hWlKKK0HHqTnlBCD4QQEtJ7Pb29Ze+ZtX5/zN5vOeekIVz9/obP5pzss8vsmTWrPKsM4f/RNnPmTDV16lQ1depUISLb7c+1Z5111sipU48fOWzYyEnV1dUH9uvbd2g6nR4JwtBUKlVbXV0tnueTUgqAgJlhLYOZkctl0dLSGvq+v6alpaUll8utzOfzi9euXbvinXfe2XD33XevANBS/kIRIQB6+vTpUl9fzwDk/2go/qlG/+oO7FkTmjt3np46daolovIBHnjllVcdcvTRRxw8fPjwo6qraw7v37//wD59+iZr62p6fKQAYAGYAUXuhAAgcofawah0dnahqalZcrns5qam5hXbt29/fdGi99554IFH31i06I018XVKKVhr9axZs2T27Nn8EQ/CR9r+nyCAadOm6Tlz5qB8pR955JHjP/OZiz950EEHfaJ///5TRo4c1aeurrp4jwAwBsKuwbCQiPteIpD7dIEiKt0QEQMRIAK4/5MoBSFF0EqT51XSRxAYbNiwIdjesH3BylUr577x2oKHf/e7X74ZPREiQrNmzdKzZ8+2+DfkCv/WBDBnzhx90UUXWWuL8z74d7/79fn7TTxg+pgxex09dq/RyfgPhsHGMIfGEgsrAkETkZtfwo6WodrB+ehGiAgEgESUISIQFiEiVorE9zxKeNDxfdu2N2DL5q3vvf32my+99NJLt91+++1vx89jZg2Au3Gvf2n7tySA7hP/jW9944STTzzlkr3HjT93woR9BsTXhSFMEIZkRRS5FrFy91m9fZxI5dhXXENUfmHFOYn/L2U3SUQgIgIC+9oT31eeF1HVps2bZfmy5a8tXPjOHVdfffUDALZHffi3EQ//VgQwZ84cPWPGDMvMAEA33njjBYcdfvi3Dpg06ciBA9y8BwbWhCEEpOL1zd0nlXb8Wd0JYEf3xdft7rmyp0CEOeEpTiS0RwCYBQsWvN24cOE7t99ww09/u3r16vXRvZqI/p9RGD+2NnPmTCUiMTemm2++efrChQtfz+ZyIiJiRbgrG5i2rgJ35ELpyhnJRkdXzkhHNiwenTmz06P82p1d3/26HV2/o+s6sqG0dRW4vatgCkZCidqyZcta7r+//ucHHXTQGMAR05w5c/SOR+fjbf9qDkDi2LcFgBtuuOH8U0455fuTJh1weCLhw1rYfCEkIiiQqlgmZVzY/exldaKX6wGAi3cRqOJZseio5MwCJ/t3tPLL9YXSPZVPEBHxPc+mE8oDgGXLlrW9+uqrv//CF77wawDbI2WR/q/Fwr+MAGbOnKmuu+46ZmZ8+sJPH3PlV668dvLkwz9ZVZVBaGHDICQQKUJJBn9YPikiIJGiZm/JqWFKBExS1P6j/5OAoOAMeyIikKrUD7BrAujeitdYI57v2VRCewDw1ltvbX76uadnXfP9a26JnqF7wTX+/9Xmzp3rRb/W3HPXXdevWr3KioiEVmxHNrAxe+3O6nfF3h1LNtLeFUhbR0HaOgvcmQ2kELD8My00Il25kDu6AtveGdi2zoDbdyA+diYSOrKhdEVHR2eB84ETDblCXp566qlnTz/99MmAMx1nzpy5IwPlI23/pxxg5syZatasWSAi/u53v3vEueed++ejjzp6fwCSLRgWgQaoQtEu72BPdQsgETAYkd4ovq8omdDF+/ICtLSHdsu2RtXQ2BK2tba3b1i/qba9o1O3dGR1risLEYHWWqpqqtoSnqrtU5XBqNHDm4YOGWL69q+trqupqepTV636pEv9CY2gEIRiRKBJOZUUAESBIM5sRE8RpSJT0n0AgcVKKpnkhIZetmyZeeaZZ3545ZVX/g/glOLp06d/rNzg/4wAyj/m1ltv+8kZZ3zq6iFDhiQLARtr2VNKldncu2oEEYDFQEGQTiWho/XS2GGxfPU68/aipfb9JSva331nYd+NGzY1NWxrTuY724EgZyGGANMM0CBA1UYyQAAJAQkcFqgWQicSqqp6SL9B/RPjxo/9YMKEiTXHHn0EDj9sUtWA2sy4oQOqPR31N5sLxVoBaU2KCMrJ/eL39CYenPVIIEVQEJtJ+0pE6Jlnnp37ne98+8uLFi1aPnfuXO/EE080H9U89BzJ/4MWf8TEiRPH33TTTb+bMmXKqQCQzYVMpFS5ArcrAojsbiSSGkmtYACs2tgSvv7Wovy8F17Lvf7qW7kP3l88iDtb8kDYCKT2hucTNNYpzxuhyNNEXMT9mIgqjXuABJAIIrIcAmFoEYTtQKEFsItBfTuH7jX6yKOOPcKedcbJfPTkAwePHTOkb1I5iDmfNWCwKK17mKlx/7s3IgVmK6l00iY1vMWLlzQ/+ujDl/7Xf/3XYx+nufixE0Cs1HznO9/5xGc/e/G9Bx10YP8wZBMao0GKAOqhUHX/SgEgbEEQVGWSIADb2gvh48+8yg898Hjnyy+/3NqyfsNgwITQfg0lU57SSgjYKixDISIs4iaaIEIxmisR2lt6EZVeKgBAUHCLlACiVoIwi/SzQQCEue2A2aDrBnWeespJyTPOPH3U8cdOHnbg3kMBAIWC5SAMCdovfpVIb9xAnMrpZAg0ia3OJHVDQyPm3Hf/9V/7zy//UGmNa3/4Q/VRWwkfJwGQiBAR8Q033HjVRRfN+MWoUSMplzdGRDzuJusBNzDOO1dqzAwCoTrj9MbX31uDv/ztATz19LPL1767KAHISCTTWid8AAQWC+GYjygUF42DX4lIIQaAxRl43bod9Uq4ZFpGTJzih0AsEWnnOQKsMUC2cxXgVVcNHu5dcN6nNl8045y9pxx7WDrlAV15i9BYaK2L6GHFG4v/psjYEDBbqa5JCVlWjz766Jzzzjv3c0RUuOCCC3R9ff1Hphd8LAQgJQuK58yZ89Mzzzzz++l0mnO5ECBSzFwc+t7sdoebO0WqNuODATz96mL5y+13dzxc/6hXaGnMIJmEl0hAoMDMEr1TCERFk41UkcZilk4EiNJxR0HCpTkvDogjgCLxSFEwRViwkJs0EQiDSAlpTxEBNiwY5AoWwOJpl1286JKLzj/llKlHDU558DvzAayhIpHv2GwkAAwRi1Q6FSY1/BdffPGl00477eJ8Pr/+o1QOP3ICiDR9ISJ1/4MP/u38c8+9iBkmnw81KUWW44EtEQBF7E+IIUJga1GVTsLTwIJlGzquu+H3/sP3PgjkWgiZavK8dILZiDATUA4QEUgpEMiBOqoEsAkUYmlf/OwiuFMCAtzpMs7gNIWIIAARBopAkZR8BiKiENG9QpuI7eKurmZAwqOmTu33/e9+rf9Zpx9XrQC0dhREKU1EEevvwQspeo+TSQnfC9MJ5c+b98Kqq6/+5ifeeeeddR8VEXykBFA++f948sn6T5522nmWEebzxlfK4WsRzl/qAFE0CRrMIRQRqtIelm9sxA0/u6Xj73+9c0nY1nSgztSkoRUsO68qwbFyAYGUV3yekAKEACjH6YscxhFHLNmjqyv7UppLlENPlbZJTAzuHEk5EcRiQwQQUtoDAbDZHMC84rwZn85/6+or9jt28kSdLxjkw1CUSlCRuaD8/WVdJILSZGrS2nvjrQWrv/vtaz7xwgtPrf0oiOAjI4CIBYOI1NPPPH3fKSefcq6xCMPA+MWhEyl+ZgXrJwJCRnWVjywgt/3t0fx1s/8317BmaRVl6pKe0jDWlkSGIggRIBqAAild5rUjEBSINJhKAQBCuvheJRzpCYCgxOopmohyApCemgoqCUciUSIAh0WSIbDzEgJQygMRG9vR1unX9Ov6r/+6Wq76xhcG9Ul7yZaOHLTyu49l2dCUUEalYGsyvn7rrbfXfPWrV33izTdfXvPP6gQfFQHEmD49+NBD9517zjnnGIMwCI1fbvZIxaQrKDBYCCQW1VVJvLd6q/n2d3/a8vT99XkkU329RKLKGEsuBMOtaopWMpMucgHH6kvqnDtPpfcRAUUCQEQAbuIFFjFLJzju4vobs/ye7BnFeJJIY6CIjbN1iqUIwIEjnojjEAmUVmDLIl2tWyYfd9yqX//ip/sfc/iEfh1dgVghUkRFAKmku5arqQJAbF1VQs+f/8qa6dOnTdm6deuGa6+99kNbBx8JAbz11lv+5MmTwwcfeugP555zzlfc5Ic+hGB7MV2JCASCZUbCEySTCdx633P5H1x97damDetG6to6zdYpQSBAkYZAQZQGIZps0og1elERy+/G2gllTrayOB7FAsCWTT5Hcr9EAFKS7T38APE3ACWLIm4ShR0RGwgsRDjyQ8QzauF5HkxHJyiRXHbDDT/qd9XXLx4oVpDLBlAeAVJ6ppSvmYj5kCJTk9He/Fdeee24Y489RUSyAOTDBJr803jzn/70J3/y5MnhLbfddt3ZZ5/9FcsIC0HoO9FIvZo9AGCskaq0FkMe/8fVP5MvTf8CNzVsG+7V1XaxtSLC4pwwPoQ8CHkAeSDlR4dyq5ocQRAUCBqKNBR5UOT17GzURGzFyu/eiJymrhQVdZTuh2sMwEaHe5a7R0GUD6gEQJ7TSyIxRdAwoRWVqQZ5et/vXfWdPp+59FurO7pyubraJKzhnUyiE0nM7HXlTXjsMccc9eijj/0tAoncQOxh+6cIYO7cud4VV1wR/vhHP75s+rRpPwSRyedDD3ABlyzotv6dVmusRV11klZsaqMzP/3Vrtt++bMWXZvOKM8LbSBJAERKE5EHKB9CPkB+9Lt2B+Kfyn07qejzqXSU/VqS3QJQzP53tWAUoMo5S7fnx99UfG7Z30kBUf+hPFB0uL9rYmZhJvH71LXd/7fbs1NOvGD1e8vXd/WtTZE1pgRJFRdRNJ6RSDFW/HzI4ZlnnnHurbfe/nMiMn/60592TPU7/sIP1+bMmaNPOukk84Uvfem4GZ/5zM21NTU2lw00EZEVJ3+FJIq0jWWls9n7VCfx1spNzWececnqFx57vMbv078vGxFhpEGUBGkIPEAn3OCRBmm/uJqEFAAvUvbiwaZI2UPxKIb5UmwYitPUyekjUhQbToa7Pquywz27JwdQxaPYosdIrHsU6UA7TgDt+q98dy5SbMKCGeDV9t9/ybvv7XPiSdOaXl24rKFvXYosl0SRCDlLVNxXuIVFyBdC31iYCy749Ldm/vBHn7niiivCmTNn7hERfCgCmDlzppo2bRrGjBkz+Ctf/I97x40bq7LZkAAi7qaPOhHqJiFkg7qqJD30/EKceNy5WP3+e311bZWY0BJBEUGTGzAPyvMAFcl55Vg9IT56TsquWoUyKgSKRAUizz+RAsErOyJ9Q2I5r6NDVRwOECxd655T6muROykdiYJINCgfCu5eE1rrV1WhualpxNQTpxeefuXd9X1rPAlZJAph7tGcNa3RlS2ourpqvuTSz/7uzDPPnDBr1iy7J67kD6UExvj+gw8++MS55557eqFgjbHiMQsgVK7DAMRQSoNDg9qaJB54/p38xdMv78p1tGdUKpGWMACRF63i0kCRihQ/ROehIu2+7NGxibRDAii3p0uUWQ45CzMgFkQSmZAxRM0ASTGYpPKJZZNC5TpOxBWksj8MdiByFJRCwhC2UGLgFFAnkrTWYGNBpvDKzXfcfNhlF56abGorQGvfmatlOEWsm3qKoElsVZWvn3n2uQWnnnLyETEEXzkAvbc95gBz5szRRGRvvPHGK88444zTA8MmYOtZidhqtycSNKwJpbYmiSdfWdJ68bRLV+WybXUq6ac4DMUpeU7DF+0B2rH8IpgTafvUjb07dk07mfzSYLmOlIkDpUqSmwiiPHCkbDIpiFJgis6pBEQloyNR/Mk6OigBiY6ijqJUxUHKc8psxF2EPMDzwV4SrBIQ8iFKwzA7hqTomC9+7kvNj819fXP/uiSYAyFSTmspek7dZ1kBQhGdLRhzysmfOOzXv/3tdURkRbrPRO9tjwhg5syZasaMGfbCCy8cd9555/3M930uFEIt3Si+nDVbE6CuOoVnXv8AF5zzOeSyHfsoL+GJZSLyiKIVHmvKkRshYqW9yd+ds/3K8xTJ6XKTbQcsvEho8Xujn4hZugeCX8byy66L78UuDuVBKWehqEgnIO2UW8ARPVsQPE9I+4MvOPtL6Wfnv5PvV5siNgEU9ZwuEXEu6MBoA9hzzjrnB1d++cuHKK13iwj2RATEYI/848kn53/ytNOOyuWNtQxtpfJBFCldoRXUVXl464PNOOuMi+32zWtYpTK+hKFb5eRFJpNbNYjYZzywzvaPJzXC93vtOpW9u4TMVjDA3TKRe7+Gyv4kO5Q2gt6ldbdnSexPsGBFEWhkAQ5AEgIswmRJaw8cCOpq0g3Pz7u/atKEUZmOTiOKFHWH04UAUgQN2LoqT8+dN++dk0488Shx4Y87FQW7zQGiybe//OUvv3byyScflQ/ZGibN0pOKCIJQGOmUwuaWTnzmM1/G9nXLtJ9Oe2JMtOI1RGmwdpox4DtolzRYabCKAB5R0UEgQfEof1v5KnMhWdHvvd+wk0a9HrFmv1Nx00NE9S6uLHlg7Q4n4nyQroJO1YASVWAigkrACkQlFFq3b9xw2Re/+XxTa5epTjp/iTN6SjY2xZYtlM4GMCeccMIhv/zlL79BRHbu3Lk7DTnfLQKItEqePHnyyFNOOe0nntY2CIwqadXl/ji3An0QoDUu+89ZWPXuAvjVtQiNJagYu4/Yfq9mVuVA7yiK5t+v7YSAEJmdKpp0lYLnJaH9BFgEpguwBQoH9Klt1YoYIFhj4Ff37bfwldeOuPyKH2ahFfkgEREwoWKsRFyGcyEfaK2U/dSnzrj22GOPHTV16tSdWgW7JQJirf+ee+6568ILL7won7fGsHgxZiXdYuaZDfrUpPDN627hX/33LPbr+nphGAJKQIg1fj8y72INv4TbS1HWUQXPrYjLL/fy7YCOHdr3UbcdEWIlhFs6TU68RQKMlA+tfRhLkGwnkG0J+g+u4lNPOjZ16WdOt0Mm7hOe+slLk9vXrSflaQhbJPwkCq3bc9fc8OOO6753+aDm1pyQlyDqtgBdcKuCr8lUpbV377333jtjxowZspNQ811ygDlz5millP3iF798/NSpJ11oGTY0xotR1O7DYa1Fn5oUHnxhUfib6/9no1eVYWOMEBRIvBIrLU6+ewaTBpMCozxEzPE2UuLMtAouEOvx7DD3GNsnLh67wgh2jh9UuoR33MpWuiodpBSgfZCXgNYpeH4NlJcBh4SwLY+UZnzy5ENw+y3X4p0378dPbr4BqUOn6Bdb61IF6xOUFwNREtpAvNq69PXX3lD7xPNvo3+fNLG1zjLq5jlkYQTWaMuwU6acOO3yyy8/Qilld5R9tEsOEFPPs8/OnfeJT0yd0pUNLDO0s/d7smrfI7TkQnPcCdNp3fKF2ymR7s+WElS07ck5dZRXIgCKAZbSh/TaVSljedTtbx9J28nq3uE1cQCSgsBDjCZAaSjPafkmJKCzC0gIDpo4EtPOOAbnnHk8hu4zHptC4M31wJsbcmi1CaQ6N+HuKz+HoNAJsAFxCIChNIFzeYw/4BDMf/7vSHseCoZ67bNyoRC2ttrXjzzyyHPnnHPOyZEO18P5sVPYMF79s2dff+IRR0yeYiysiOiegxKPjUgy5dMPf/DL1rWLFwVeXe0wEwala6PAj0rP3b9TK1/xJby/PGq591b6Lk8pkOchBMHmQiDMm8FDatS555+gZkw7GRMPOQANOo3XNwHvvRKgtYsQhArZMAGb0mjZsAFBRwsonQJgi11iZuiqNJYveAOzfvJH/O6GbyLfFri42nK8S2KUUHQYsj366KM/8bWvXXW8Uuql3gJIdjoLMdXMe/GlF6Ycf9wJ7dnAOpsNRUdKXGvBWoO+tSk8+NQCc/5ZFyqd8o1l8V0Gr7OVXZiWVxGq5aKyy9yqkdZcrtwUw7bKlv0OzbGdrtZert4N3bI8aARFnCIOO1Mg5TvoWjRMLgSCPJK1tTjpiPGY/unjwuNPOkqpAUP0B83Am+sMVm1ndBjt4h4jXD8ILTJ1CWx/8U6Z/4sfkq4bBDYGIiEA6/IM2EKBQUbhyWfqZeoxB1BLZx4afgVIVBxXsO1Xm9RPPPXUs2d88pOn9MYFdsgB4tU/c+bMkw456OATQgsWRi9yxDkr/ISHpi4j1876HwZCJZRMADZS6GJ5T7te+BFx7Q6+v1ttB/780p8rwAL3/zIsgUqne95HBOVpWKPAbTnAIz54/5HhhWedkDz19OPQf+/xWJcj/+ENgg8WB2jLA8ZqBNYHGDAiMBHCy0YwMAGY5q0FMPsQ0Y7QtHuXsDNxNcEUunDt7F/S3Cf/ggQpGAiEy2MOEEc86UIIPvSQQ0/63ve+dwABi7sTwQ4JYNq0aZg+fTqOP/6E79TWVqMzGzr/fPeQZiKwsairTuJ/f3Wvff/1V0jX9VNsggi+jWP3VK/zEAdQFP/dy8SUwrN3jygqlEjsibApZ/9Scab39xjYxsZgxF574ezTpiTOPes4OvDIyV6jSuHVdcCbL4Vo6gSYFYxoWBYw3ISBCcISKb1AyBosQPOGdSloPwKLor4UrQuBNRa6pkpefe65rffc/+zAS6ef7DW0BKiIpi/rdK4Q8pBBA72jjz7mOyD6HLqhg70SwLRp0zQR2csuueyQ/fabdKphMLNEEZVUeocCYAXptI9lG1rwm1/f5KlUCmJNpMGWPGgVGrlwZMbtemWWOIEL34rFAe1ABkgcNhO3WP3Y0Uyq3v4gRWLtgT+Q6z9ASIgt/OqmmeFZZ30i2ZToh0VNoHnvit7QEqA1LwAriGgYEdioWzYKgytFfjlAxyMFExTQumUDoL1I7ERjRAQGRVLSpdCRpj4//8Vvlp5/9on7JJM6GYYcca6yhUNAaI0O2ZcJ+00674gjjhihlNo4c+bMYghZr2bgnDlzAABnnHvmV4cOHaQKgWWnbZQuj613FkEqofCbP96N5rUrcpRMufx70oiDNv+vWwWo9JErm+wmhxhWkHxpg189p7Wff+P8Au5+K4/F2y3acj6M9RCIh4IQQqtgLWBFgaOgYuEIAbYAWwE0IehsRrZxUwu0roxViakxEqPCIJ2uTi9+/a0Jf77jIa82o3tEWwMRnUJRvgC7z/i9qi+++OLPiwimTp1anJTeZkcppew+++wzYPz4fS8UAcLQapHulxPYCqpSHhatbcVdd9wNla5OC9to8mNHye777HtrpaAIqfj3x9HK39Xbe0rnXLwia+Dun/wvfv61H6E9GyBRlYLJM/IMWKthrCC0FqEFQkMwFrBWYI2AWYrEwFYARWhp2M651pa3HQF0U+giCyoOeGW2oETa+/1Nt0tTe4BEwutVbAIKQSFQBOCQQw6+GICeOnVq0RLoQQBz585VIoKrrvr66RP327c6H8KCNLEAXIymcaxWWJBMKNx978O51o3rWCWSJXlFMTvu3qHoz2UwvQNxDEAWRAwljjPvWEOXCsCnAvxB2bEjV0D5PeWnywi14ncRKGEocc9VYCg2gCnAq81jy4t/x7PfuwjbViySZL8Egy3HrJ6NC35liNMBWMGywFhxSmCkCCoPyG1dS1woTCXlKVDZAoKKEFTnQINyOJxKpWTFu+/ivoefRl2VBsPNDxMcRqOcBWNJVDYET9jvgH2/9KUvnUBEMm3aNN0rAUydOpUBYL/9Jn3J0xpBEPY6BVaAVEpjS2uO77nr3lZKJCI2FMv2nhGzO2+7g7r9+zQnuy1MkAWlk8hvfg9vzvoCLXnsPmUSnrKWIcbCWopWe2nVM1Pxp7AL8fI8IL9tPcGy3rnUilPcFABRpJT31zvqkbMMHYXHd2e2RIRCwfCAfn1w3PFTLgKAOXPmUOlpUZs5c6YiIj755JPHjxkz9khmiLDo3liiWCvppMYTc19vXPveYq3SVaqkuTmzj7orfx9h2xWb/mfERM/+lj1TykRB5KGUMBQkqyGma82y31/8tffv/90riaQgbxSLlTJ5T2WTH51jd14E6Ni0FuUWSM9+IbKk3SQzCyidxhuvvo6XX1uM2iofVqzDFrqJMmOMEgHG7z3+UwAyWmuDIhYbtVg5mD59xrmjRo3yCyEsumXrxg/UmqgtBG695e6+UDRQCGClSCiOp6OKgdt9InAyFhEbj1mvEgZxdEgl6+4tk8Y9qad/YHccRDslIkFRv0HskROCCkP2PGQANfjeL03ITztAobNgAeUEkktiJVgWWEbEGSIuIAoUWulq3rAWfgo7NHglsgiIIrOaoDwF29Uuc+of7dLa8X+p4MDuYCjVFUL22nuv4V/60pcOZWbMmTNHdScABoCJE/Y9iwgITUAiPbVLZkEmncB7S9fjnTfe9ClTTcIG5fDpx9OcmBCRKJavUsf4KJTDPXmGI1W3JFUipYL2jrqrvvGdsydNOfmk5xYHktKeCuE4hQuTj8VAxAksgS1ghWC62ii7dfNgeN5uSkI3zswsSKbpmWfmNjd1FIJUMtH7/UQIAmMHDegvJ5908qkAsNdee5UIIGb/hx02YejQYcMOcZgyq96UIhGWhCbMe+HlpkJTQ97X/kcivXc0iT0086INvWvNvfv9vT2v4mDu5Xw8hrE2HrFhOG+dTqTEdHY0TTnxyKd+8qv/3us3L+RlXXsCWgnEqqgKubjJtpEZKGW4PRG6WrZL0NKSJq2KMHt5K2EoPb9NpZJYt2x5cvG7SxtrMgrcrXJmPGYmtAoAjRoz6lMA1GGHHWbKOYACgEsu+fKUUaNGVxdCWEBRt0cBIHieovY846EHHquC5ycsGCAfLsijl5nttZWxehGQRHG/hIhVl47etH0FqRAPJTEhvR699qA34ulG8CXCjwrHCVB0/qgEtJcEG0U1dem1D95/86S7l1DNvNWONNhagF3MnjCBWYRFs2GGjXQAGxohDWRbGzrCfFsjtN8rGyqdcu+WKGSORLl6J9b2u/O+J2pLQFU3BFxcteOQgdo+/Q467LD9RhBRCRacNWsWAGD//Q+Y6vsaQRj0OmpWBOl0EkuWr8Pi95alKJ1WVmKE8KNk/1J2/Ps1AUCkRJFHXOjc8kj9H0YstgPG3fNWQRgJCkJxdr9xq18phvY0CUSJ8mBCA2sJxghpHwi2rq4VKwOiHMZdDCShfLzZiiCR8l56+bVCSzYwfsLr1YImIioEsKNHj/bOPvvCQ4BKK8AC0P379TscAOKI0qI6Eq0OEYukAl5+9R0UWlrgeV4ki3tnWzsL8eqdXe+eHN5Teb87oqK83zs6Xzognu9R2NTQcdsfru0YcczUwf/7bJbzQYJyBcAwgUXBQEEsxOvjyab5d7Vuvfdb8zxkQb5vbcgQ1gABXds3ApR0y3ZP15EAKpnEqqWr9KLFq6mqyq9wDonEBY8IJgwkk0pg3LhxJwCRLyCW/1NOPXVsbZ8+BxkBWFzwfNHtGw2KIgUL4KVXFjiTCFFdvDhY40PBr6WOAt2/vzcXsHSzX8of1fvE9gb5dxOUkf+Cig4qKr+uCN8LiBQ836dge1Puii99OvvJiy/e55onc9LYlVTZgGEtQEIwpCAmQKJPAltfepq2zblmHUxbJt/exuMv+5UOMtXItoUo5IHCtnUuJyJyN0ssInvpd+xCF7BLogGT1oDpaFOL3lvSMuWICQPEWhHSJadD5HMImAkARo4cXcEBFACcfdppB4wcOUKHFrZ7bd54bH3PQ0NrHosWvgckk+BixbXISfOhxYD0cnyYe3b/KA/X7KldVXajzNCETqUlbG4NzzjjsM0//8NP6n7xch6rGxMUhATLHoQVDBgwARI1CbQtW0Ab/nrVYqW4TdUMDTs/mKuW/enLi8KuBviZBFShU/KtDS5dsChO0bNPvbVYMXVUWv3mGwusQ5Z7sjEBYJkVA6iurZ0EoK8CgKlTpwIABg8efHDC98DWSs+V5EyYdFJjxap12LhuA8j3y7x2KHZ8V2z24wCGir38kEBQxNZ32bTvwzS30+GH7t16+52/Hv7bN23q1TUe8gbImzgjmiFWoOsStqt5g910x/dv5/z2hzlT+3XJ51+mdPqm7LL75qz769V3Jm0j0lWKcm3bXMicdTUFdiUHSuNdXIACP63ee29pe1tHYDzPQ2UFNHctAQgs0K9v39ozzjhjeAUBDBgwaCIAhCHTjkwkXwHvLV0F09EB5UVFUWO5RdzDhOkVRdztyem2YqUM24/tqG5H+ap2LLKU47crHcBZI1EuILHzdxCiCTHQnobt6OS9R/Ztua/+l/0fWF+XenyxwFiNXOiwfhGBsRaU9CVoaaD1v/+yBFvfTkz57/tmofX9dyVc830KO7ep6rE/Lqx46sDlN/1HuPbxWxu5o6kTnldE+twn78RdTiirkaDAAiCpsWLlWt3U2FJIpnxwFLnLDtKN1DhN+QKkT9/+yZNOOmlUnLBuAai6uj77uBuKVQvLJq105r33lwnYxvYSKq8V7Ip696yVmz+9nN7h9d3b7vWpWNCxpP0ApKC9hNiOVh49rG71vLm3Dnmpa4S68/W8hDZB2VDAHKV2sYWXSogNW2n9H7+SC9a/MZ8y/T7zwg2XhkiPbiChSQIcgrDQRJkBC4NNb6zftu6FZmQGnw0wg/Ru8iJErF/BpZ+ClFbIt7WOWLN6rRo3djA6s6WSM0VOrTSYmWurM7q6unqyBwBKKUE/1NT1qR0WD2L3fD8QgYgRMLBi+VqC9ioItAKOLVfciiKCel11O4Jx/5m2Z9Bz732JtWalCEonxGRzNHRA1Zr5L99VPT87oub3L+YkCBLUFRKMMJS4yCiV0IDpog23fKM9u/LFRlXVZ39mG0KnL3XogFPuhE0jwuzD5FVfphN1Z1qSupKDYc/6Hiv7pDQhCLoaGpq6PMIIEcVKiSofdmEGE8FTCgMGDJigZs6cSSKCTxzyiSHJZHIA96KIUxQk53kaXQU2m7c0bINWACvpzecvZf8VwbNy/t39KJuAHbPp3VXuSoRUIS72ME1MOV4M8nyYbIH61yTw5BO3jHk9P2LoL5/KIZf3KBsAYtjF97GF+BrWdNlVt3y1s/P9f1jK1HUK24FKeVCKDLQypD1L2hNSXlrpxD1Qcipr6quUJwTdiytv52NWRCcp3v9MMkuXr/ajASVBZYBtJKIIAPr1G1BXNKaOO+64RHV1FcId+EoEgNYaHV1d0tzc3AKt0Aut9JysjxQf2j0C6PWWPQSWBID2U7AdnRjSJ5Wf++Tvsa7P/t6vn+sQa33k84AJGSyACQE/4XGKsthy+5VNucX3/p0yffuKtRMBz2ckfUbCE0p6rDKaVYbEq65infaEkp6ID1YJEpXaaW2jnTeK6EJRY2tzuzvTe2MRsgDy+fy+UYYiGLB7V1VVk3WVGHW5Vh+NiGitqLMzX2hpae0DzytaLDub395Y/Idl0R9V660flSIMUL4H09Ic7rfPkLn/eObWw97Ijkj+7pkc8kEV5UJCyAyJInsp6VkqtOh1f/1GvuO9f1RTZsQhYGOUUluIOF9dk9QsGOc8q9rZ+kIBlFYE5DRkXRjacZ02lQZ8IRSoe592p7n514l1azb2DxlQWpFAemyqBbh4jqFDhgTe1KlTMXv2bEyatL8kk0kUQonkdY/Hk/aAbQ2NOuzq8pUmF/snshNO0O0Je/BBe6IbVBLUrpXASlkfC9DYUFLwfB9hc1th0v7D18578c4D79/Qp//NL+UEkqJsXhy8K4A1FpRJSNi2VW+65+p3CsueG6Oq+2kxQYdWvph8tm7WL366+bLppx3Q1tEmQh6FIhDLAMQDKUp5Kt23JjP6jVXNic9dfDU6GreS0h6KXtgedeJ2MWbaQ1tbyzYTco1SyrfdavZEqCAxA6G1Q4v8Jp2uSXhaoRDseG8CrYCmppa0hIW08lLde7fTzv3ftT2zAiqv9sRPJChsbgqOOW5iy333/3H4bSv6VP9tfk7IJimw4swpq8Fs4NUmOL95uWp44JrfFJY/309lBhwk1igh72R4PsCUx5ARg5/JD6pas6W/BKKRi4ZXoio1voIngppODEai7wBg20ZAf+hNxARElMvlradVgSB+b6NB4jyTWnt9vOXLlxMArFq1YqL7a4nMylcKR9phIZ+NQlHIxTLtSgZ8mK/oxinKLYneWrmF0f33XbZixC1Ea5/Chu12xoVTN/3v73405k8f1NF9C3KCMEG5wIWlu+CKUJK1CZNb+Ya/5e6rnjDbP2hDelCGTcFAKR9aGKQ14FFIHL67kfHa4gDieYC4clhSfDcjFwAD+jGERcBOmes+cbIDf0u3iwAC8vmCBEFAimI/TTcuGcEMyWRSihwgk6lWAMr84aWBV6qU1BEUAoAd4l8xgLvRdjQhPfzfRRrs6UTandIwpd93o28CaK0AeGS2b978rW/OsFddd82Y6+creuGDQBAmKV9w1buN1SCwJPskqO31x/xtD3w3K0HHfpQesEVsYT8iSgggKIZRMawl6iooFISQhC5CtwRE4AzBIwWAWSu7BMKTJFbLimNROfkVUU8xCAYpPjvIZWHCMMoviP8cF7+iKH8A0LpM5ey+UcMOx6sIU+7BIO/6qR/BM3pru+qbwPMTYiwJ2psbb73lmuCYGZ8b8tVHc7RsM+CJT/kAsBSCDKC8BMPXqvH5P29ufnT2TPLlU+RlzhMxl5PWEYKoCKREInvfGCA0LjuI4YJA4wANjpxOzIy4yD1gUDnhezg24gpVF0komvz4b+im3BcJgNns1uv8KK+fu2Wllneg2HYQENqdxffmLo5+i+DL3hW4f9KSEM/zKOzMUW11ovG5V27tzI48cq/L78yhodUDWCEUgo1x/VTS6qBdNz36iwWtL82eiapxXTDIQqxASQgiT6CUq3pSql7OUbnUOP7fBYLGxakBKBcVJKKI2QyAxLUOZAdcrbIVk1SLw0LwfCLS3eMxK+8nuFoOxWXf2ZmN/rBjw10ADBrYvw3a60Bxo4Od2eEf18r+MK2SMD3Po7CxqXDo/sNWzH3mtrplfY/c66r7s9LS7gFGw4SMwDDEglN1SQ63LtPr/nz5260v/WKDrt3XwPDeArsBJESkPCGtJC4JW2aRWBGE7AjBWoa1cWg4XFQQK4gosIhoL7HR9TOqPbxTq2kHYy6Mmpq6MJVKi7Xc7fqy3wgoFAolDpDN5tmNTUwA3eWtC2DMpJNpJLwIXuylgz1oR3qc7A4L79Q8jHf2BLAH8WY9mrCCUkq01hSyQdi4vfUrXz6n/Qc3zBz2x0WZxIPPZsWHT2xUFKolkKRmnYZqfa2eGx/96WrObtymqga9ZPP5hwHOkVLVUJFXpiwMPpa5gCC07LyExTDwkqOV4dJm44BRYesV88bib93BJ5crhRTNqCjHaupq6toEOsHs6gt0j+kQESgFBEEh8PbZZx8BgGHDhiy1zqNAcTmW7lAsM1BXW03JZDJfMEF1WezEjge+F0KpDDDtffJ3R9krH4xdiQMigu/7VOjMI+nZjjvvmd06dOr0MV993GDJhgCeSlBnQFCsINaAMgnmfItqfOa3H3S8+Is55NdNR7JOsbEHQ3lJkCRBKtq7IA4R7a1vCsYChpUDgKwU14QQSlHCRiAiArGRHK8cnx2JwYpvBAiWUdenrh+ABLPtsdCisRetQdXV1WvUvHnzAADvv/9+kMvm4OkoBQw9mLsYKxg4aEAhXZVoKX7IrqTAbkOwu3PN7jS3jYwr7SaAsFBUM6WwfVvL8Yfv1fTiC7cnWw64aMzn7yzgnTUEZX0UcgphwcIK2UQ/n83aV6jx7i/+sePFmReq2kFLSas7gfANaPUZKMWktEDpaAOquKxtdzBKYMTlBAoLOFYGiyHhVEwWscVS4KXDKYUucLYilLh8nCoWmALY2FEjh3R5HsT2kjAKACCBR8DmzZs9L3oD8tauzeW7kEqnFTv9lMoxPlFEBWNQU53xRgwd0Ny6vWUkKW8X7Ls3i7Y3r2D3ye/dc7hr+iivAC4QguhkikxrAyvP5H75q6taTrn08iG/fS2RePj1nCTJJyWEvLVgEUEmyYVci2556Kag7ZW7EhRs+QBEi7hgB5BSP1GkBovyxGn6zsMlUMWyCeIiCMr4ACEQZwWIjVJSbDyicFvaEEGEXLIIbPQgdpoYxZZB/P1OPJfzGSk6uoAI1Q9GDB2QBlwUhIIiLgswcfU4KUoxkdVFHWDBa6+1trd3BAMGDEj0NMzd7cZY1GRS/pgRw1KLFywFpRJEzDsngvK2Q73ho1j5kZUbsUqtPYQsZBq2t37i5EnLZ93wg3HrMofs9Zm/5LGtJURaJykMGBKKUDJBuhqUW/KUbpn/p9vNqqe6KDmoDpl+s0n6HSwctAilx4qK6v9XcPpoipQDdVwYX+l72FhY4zKBQFQs+u9CZ1RxPNgoQCoRQAfbllk9O4Cz41NROZ7cqNGj0lK8rKfipJRL9m5vb9/mzZ49W4gIr7766taOjs7tBIwAYmi8cgVba+Er6EMPmlh47P4nQ1LKR/f68DttO5vof8Kki92/iApBK4WwpSWs7VfTdeNN384ect7FB/3qFS/5+Gt5qdWafAJyoRXP89mrgi40ruLsc3/+oP2VW/6hEK7Ffp+/XVY/fqVAXUjMMxK+vVVU8ILW3kjACogJ4rJw3fIHRFR1iOQgkBFIHogmN7ACYwBrHKm46Jx4cjkChBTMbu0OLDv5XTnp4Hlde40ZnY62ROo5qOQgfStAY2Pzcg+ARBlAYWNjw1YAI9wnuflndtTqXiPCAE2YMK4/iLqE0Cc2KXrrW/e375hTqN1AFCsxhQqlj8SVZaMEwq4OICy0fenyMzZe/s2vjXo7N3bY5XfksaUplFrlkQkYQp7xE8rT3KHbXrsz3z7/T29y+8K7MWDCQ2xHZLHkD52Jmn0KQVdn2+VXf+3dH1z1uekQFYYQZTkqjBGPCDNqkyksWrc98ZnPfhvtDZtIKS8GYMHwHRBkIgNPYi4faSqKXJAGI9rIyhGyU9i6jV+5qKTK8woK1jDSfWuyQ4cMGZAvCIgUlWMqFIlkrUh1trfz9qaGN2MRoACYtta2hQAmK6XEpS5Xrm4iQsEAEyfunVK1Vc3M0icKvut19j4K0Kbivl5AIaWUkJ8ikzfgju1dU6ZMXD3zR9+r29bv8P2//azGe+vzqPZ9pInJGIb1ElDGeF1Ln2zIvn3L/MLapzeRN/B0ZPYZh/ZCDYJnt3jpEYdbomngIJsbtu/I+9uGD2rYCuQMkAscF4+SJqAISGggVAOgU9WAyQOJKvdHUmATcwCBIo50OccFoMilikkEDhV3EHSh5zsah97GlQhAGGLcvmPyffv1y3QVerKUiAAkmfRoW2Nb12vvzH/PA4DYEti8efMSFsD3vR5eQRFAkaZczmDM6BHpvfYauXXl8g0gP84K3wM53g2O3M0byriEGyDt+QiDkNDaZPaZOKrjB9+/SgYccc4+v3glnXzizXaklY+apJIAYJ3yVUKDulbOb+9aMuee7KLf/BrAkuqhZw7obP7gEZjCoYC6AKnRq1nod4qoPygTtre1599azfL+SiueEgpDiip7xSaVAougX20BYjoJgCtzFw1HaAFjALYMq+CydyMXemwiMxNsaMGWuysYuz0+RAoICjjogH3SyZSfaurq6j0XQhhJD2hra215vP7xdg8A/vCHPwgArFix7NXmljZU19bpQmBdWVehoswiIhSCPAb1rcocdsCEQSvfWwWVrCYrFuU7cuxqHkuBpDtCOSoBqBicEoJAESmVgMkb2JbmzvEThjRedtmlA/Y5fXrV3A2DE/f9OYvGlg6pSXhEQujiJKUttFn1Gre+fbvJL//HErHrvwdSrTjhOa/zhRMbATxLiTHfg1InISpZS6QY0L41oS9QsKElL6mhNaBVaRIJCiEEJNHuJgBUpMkDjEA0bAQAseUiAQCR2krkdMcCkYS2X/ch6Y1z9jxHAkUEDtpOOP4IF3PCwmVbpRZxB03EGtAtDdvXAOhQAFBfX89EhDvuuGPZpo0bm5IeiLupj8WXMolWhNNOPs7CmtzuegOlGD60e7K+4gCgtILWPnE2hGlp7pgwtq7zf377zexP7n2wz5rxV1ZfeU9t4uYH2qTQpblvdRraywhZBVk1t7n96W880HDvjFzugwffIU9bDDgsDWHghRMNqoYfiPToJ0R7J4EQiCILRSKkFcSiEDCyBcfC3UpGRW0fYwXWujxAV9aeIeL2DIQQbOgyhZwfwAWTxJCwWIANO3zAEgQq5wZrx2PYux4lwmygUsntxx11eCof9O6GIQeBCwC0tLW9ApScQbEi2LKtYft7JPtN9ZRitpWFIQmAUh515RlHTT64X7p/vyBvwrK/lhSOyrt6TnIZO3Cij2KWGP+dRSkipTXCkIGWDobH7cccO6Hhoss/59fsfeywF9YNrr7u1i50trdJXU01Z6rSGgCpfCvMmueRW/r3Vfl1D34SQBKJvf6sEn0HcZgfQ52NNyM1dhjALEIHg7QHwEJ5CedSjcvb2ZBJh7k8MiYU0V5cm8KBYBxp8jYC8FAEbixiJToIgYAAE1oQdNk+1AJN7KxxFlgDiDGDY/17TxoREedz5sAD9tk0fPjwY7LZEFrpHmFdBCDh+5QvhFi3enUFAWDevHkKAL+/bOXiUz9x4tSEp6XAFtQ9SlkJstkAY8eN9o479pCNzzz5crXK+KgAncrt+u5maARBxOdjOerGy+12BK0BmyCb7YANukzd0NrsSWcfH5x89qcpP+DIMQ8tqfFf/XseNp+XdLKK6qpBthBor3Nhq1kzd2vHysdqbev6YUJ2HOr2sWhf+T6C1e8zRn2aPO8YIe/k0igbgJRVpDUTXHYOKyFRBJi26j59t3cw9jPQ4rHbAdvtaholxIDBosBCkDLELlbiQiFYA8C4PD7HJSLbHwCRSx+HVaIosQVATbn7ZFcYi4hAkyLOFbIXTfuU1NZmEu3bs0ziNkgtqU0EsSKZpNJrVq4J/nbXXYsqCOAPf2gQAFjw+luPbp027asD+vVRhYCjD6kEe0SEUwmlLplxRvjMI882USbR3xk4u6/qF4NTiKKAEwXLIFsIgCDPOk3rjzh6L3XS6SdXjzhwStWK7LiaO1ZnaPFcQSFUUp3OSDoVqHzjsrCw5vk8Ohfd0rXiiSoJ2o+El+lHOjmflBegED5ePXC/y/Nd+e8I6fOgdFE0uqwmHwTSgIrKqzJANlbQyIZc5ILM7KB6dgYxR+KKRZU2FC+NUszIivUBnA1Yjs87M0Ki8nGkVWsc1NGbGVg+6WWFO0RIETyVP/bYI8eHFhBhQlll1mJirwL7Gnp7w9aF77zzzjoRKe27Xl8/nZVSuPOOm1/48hVf3DzkmMOHO0xSemaJElRXXnDqScf3rRlUs7EzR/3dqt7ltLv1ryCkPAITbGjAQRawucCv8lsmTRqZOGjy5JpJJ55R1Zac1Gfh1j7+rU8DjU1AOiXorzvQ1bKEgndeoNyWt1tty+KMLWxvE/HPIC/zrEoO/iPE1LBYgNR2hPLXju3rrgeSRwJ+F0CMisJHBMAjwEuipkq7oSrtKWwZrs6DRbEmL2JbPrqfxU2gCIeoqHwtCI2AFUAscAWjuDRMFFU8jWoG2TDsu6sR7DGiBNhCgNF7j5aJE/cZ1NZpYgSv23UEz9MCAOvWrX8KABYsWKDLg9DFWquVUoWl7y96/bhjDj/f8xQbw8p2x+ZJoaUzL/371g2cft6n1t126wOhrk571kaWwA43qyISDiGFkADJ6WTSGzKgKthnv/2zYycekhy638lUqNo7s7ox7f3uTQzctq0Lqmu9JLrWSKblvdA0LcL21jWdpmNtPy60bIDSTeRVjYVfN8J9gCEJVl8NjM4kamhwUMjPHrfv3su/fMlXanJhsFL5CRJW0c4l8VQrpLXy125oHnnbPU9VBfmCoCxQCrCeZWfKKRBIVAnNi/QehsCEBAEVINYXkYTArXZjtTP52DpcoBzwoWjzDCGwVcpas1dMXd1xgB4twmqVVsTZ9s5LP3PZpr596wZv3NolWhF149ggEFKphGpobMGLL857GgBWr17NFVkI9fX1EBG8+Mqrfzzn/E+f36euj+o0QcWD4p/CFkKKLr9sxvA//+W+JgENAcqlV/cOC5JJv6VvvwFq2KiRW8dOOKCluu/Ifb3aUXVd0j/5/uaC949/dNW2bXkCYcsq6Pw68YLtZHJbqDO7FRJ0tQgxk062K/IfRP/hP8OJP1wlD31vf1K8kCACRftIalQbQGJFv49g01OHnvGdIw/64jcmd7S5dW3Y/YyjowhAkoDqEMi8uBSFpe+QSlUBZAHovtmW1r48HGDDKk7CZInNQEG8dxMbC2FbHX1+cfTDMBInLBBlIhUh4h2RkBYB2DKrROZdCA4tTtpOBGo0qcwsipJq5Yzp59UGBtFO5SWFuuh6J3BVCuqD91etufnmm9+SaHPJCgKYPn26jf7w4he+cOnak044fozb5rInOWqtqbUtLwccMGHIcSdMXvHy3Df766oqbwcuSCGA+vStWd23X7/R2U4ZN//FBV5r+yvId+SBXIuC7QDpUBQxkc3CgGGQBMguAiUzSNaNVBy0AzSKxWxDR8toPPrtw6Gwv8uGd6MlyksBAlHJyUCfkZvz1PGTpy1vXGtFICoIHXDJ0XZnviIELOjTzyOBYnCoqETHhsULgwLSHDIsRSYgEJdjj8U8rO3NyQWEgYmsBAsWC4guzY4DApy5aCyxCXrgADtuBKVIcVcXjpt6ZDhq9PC9WttyUIqoMhGEoEhBe4oVoD74YNljAPJw+p/pkYc0b948DaDw/ruLbjnx+OOv932Pw0LgkM9ukTyGDbRfrf7re1/Vpz93UQGq1ifmImss74UQYdumjYdtW7fOiQgSgXaEBO22iGO4QvakkiVHhmA8JFQAkkI8MMISvg+hs4hoBIj6ul3EESkhqpitDiQH22z7oCqliUOB5wNJFSW+Rl8uBCSZJKE0lE4sBXiikFEQDwCHrP1sECJtDcQiSpsvG1wBg4VhOSqMTUApkpcg1oclVzWUFBW9ge72qKNMkFCThOFIZw6XlLxei5kD0UWaJChsvOY7X9apVFI1NXcIaU2x+BCguMVcdSahtjY08Yvz5t0FANOnTxegl1qu8048kYkIf/3rX/6+ZOmyQiqhtTi2UPFuwE1eS2tejjpy8t6Hn3BMs83mLHklF2c09U7vAkNpDyqRACU9UEITlCIrDGtDsC2AOAdwAcwhALdTBoA0QElnIyoWIhbSCipxIMjvB/IIpIVIC8hzerkoikeXxaOgABQCizAUV7XbAiYUGCMIQiAMgTAQiPKyKCp3FgClC125/mEIWMNUrPRt4yOy/+MQPpR/eiQuWZx4sNZZD9ZlBoll5yoOrftpWYRUh6OLnmygRyCtFnA+wMRDJqw7+qgjJza1hC6hryJxVGKTnKuSUMs+WPberbfe+rqIUH19ve2VAGYDzMx6wYIF6xe/v/geX4N8z2O1A4FkjUE6ncTvfnFtOzhYT25plfU2imhBPAAW4BBgAxILkrhMXLQyGJGtrAFBQEpDexpKJ0h5SaW8lFJeipSXAHkJKC8B7aWIvBSR8uE2oi6VqbcGKBQQ+eRRUbHbhO6nNS7Bk8OwHxC7+gSA7QJ57VFJdylW+g45QvDcJHLICAtReZBKFBtBGMCGBhITgTVOfyqrFxv9m8DSJ5q5Xse6nB5IJSC51ob/+u4V1ZmqdDqbCxhUWRMkuhSJhGYrwDvvvH0HAIm4PHolAACYPn06AOCRhx747aZNW6Q2k4iGtJILEBO09qixJY+DJ02YdM55p+VtZx6e76pdKTCIKWIAAkEIIRfy7Gzw+CfcdmuI9xBWEu0lvJ6zbf9tOruW2pxdxx2FtdyeX8ed+XXc2bmOOzqbbUdHk+3qWMcdnS0iDPYSIioOy3ZVO6wphWTHkK5lcnI7FMBaYkOwQTAOEBIR64CqwuN1Yw6aKwYQIyyhhYTRKg4Z7t8WYgy4oJ2tH7MQIYANbOhDjADWOoJhA7YGbEMwG4hYiFggZEQDElkBlUTgLAYUK4HbfJb2nbTvsrPOOH3vppYASpECU8nZFEHvJJCaqoResXxFy1//ctvfiAgnnnhi0XHTay5yfX29Fbdd3IILLrjg2fPOO+8UUrBK3D7BFUQQ6QWhBa77728MffLp+RsM1MgIhyx+RSTRShEuhKIqXtz9I96GjlyxA+7qCC79+tcT//mFszMFG6qu0BKJhedpkCJ0tmcVAzKwT7Ves6FFfeOqG9DSsJ1IxzqIAbONTC9XrtVaAWwJUyeKcf1yKBoRsuMf3L5lU2D6HA4JQ+IYwSy58kDE7tmxXzcafFLk3BqAex9bt11umZIsZRC4u3/nKmCsOirPJ9vWsO7nN/6+uramqqp5cxdrRSo292J3sguE9WxCw3t34cJ7Fy5c2CDdNpHcYTJ6fX09iAj19Q9ef+TRx57cr/8Aau8s9BrSrQBsa+6SvSbs0+faH1zR/sPv3dCq+w7oI6ZQemAEFFVEtHVPAYu1YoJyaJ2/3+qger+5dABWrBMYIqQ0kEk6tSLWyKkTfXMe4A28J5AtGz3SycgL5ibDGlewkZQrdEW2TKZG29eG0SYOxQ6RAOTt09mwNR9mALZGsVFuhZXNFJFL77KmgoDiZ1vn67dRRrAt9ql0TUQ35QDRTppWSmxnpxw19bh1p35iyuQtjXmBAnEUmlcecQ1mVKczat36jYWHH37w1yJCs2bNqnjNDgkgMgk1Eb1w7rnnPDl9+qdPzyplhUU7EVlGCERIeh61twXyza9dNuSdtxbcdf8jL5yp0tUDwKFIXP5yD/wcBA2ISCGf44ffsOqtd3NI+Q5G1KQiAnA72VgGBg/xYAttChwQSaJYQMlCQwxcrhwcDk9c2QshgQl1tBWQgGJFU+wKVTu6wQQ4hiwsiLWIKhZfcuVtAUAgIcFZDmAFpcSaZbB2SdgZHCW+gbCJ3OtFLbM0IAwoE+cCEkr7KlaShBKwAilrs0t//+vrhoaiM4VClkkrxTH0HBGA1oCnPJNJwlvw1luP33333R/cddddevbs2RV++53CTbNmzRIRoYcffuB76zds6qqpTpJlKxUfEX+LUjBhAclMMnHQkcedgkIhQaoniry7zb0jJBsUNKymjPIpk/Ap6XvkEcgjoYRHlPQUZXyilKcJ0F4kfJ1jhlwuQxgAHFhIyEAg4EiRsyG7XP/Qgk0ksSKI3bEWXZdvaRpujADWKHF13l3YX2AhxoKtdXC2td24G+UAzhpjySm+HCl+1hEDm5JmKjZKIStxxN58QMrzKWxt7rjy65d1HThp4vitjR0CTYpFioojUczUCNXVCbVh42Z+4IH7rhcRinW7imfubBKinaX0XXfd9d7b77xzs6/dfkIAirI8/mTLFpo82txkUf/gM8Php2vFxjx9z1tkRIFBMAKnPQcMmzMI88bF8AcMEzDCkBAEgCuCGXtlnFFmjXAQwCliIUOMQEyJCGJC4FDKBj2K0iA9qNDeNtoULNgYchq/U/rIRgQQmqJJVx5/B4ECaS3WEhvraglZC+LSAWZnGRhnGbgu9z5cRCRcyNPgEQNbZ//gW+NbOkIUN+OsUB5cFVWlyVYloea/8vIjd95559sAVGz6lbfdKUjDAKg6Uz0BAESEVFyXLiYDcTlw1dVJvPr2Six55w1RSeW0rrLcgLLd0io/rkw0UPQ8F21sXTZtAMDAbZQQ7cDhot7J2dSwCPNeVLApWkmWAJYOpOu2cIh9YYQtiUIElcZomSLryCVMg+Kd8Yp7JFhm44FCUWJMEfrlsj0UHJysQDZZ/FZnBEgIqP6Syw2EYgsOtZiIMMtC29wIC2A5yiZTEFg4s1u7beJFRHke2fZtS/7ywL1U079u5NrN7aKVJmEXEioANAGKCCQifWuSWLd+U+6B++68Zkerf5cEENcQPvbYY0eN3WvsSZGYUk4J6mZzCiNTpfDSy3PzNtvm+XUDPVee5MMKAQAgGEMIc0CYN1ASRc8KQymXbOmMCQtryvYsKCIyFiaMbHxjiz0uhWWjqIiKtT3TE4RzDE3MkoHlMqCnlGgRnxKR3havAosSttYhRwoitsw0KilrQsSkNEExVYgSEVEJn0zT5ubv/fDra04+5cTT129rZ9KkOHa+xalp5IpPpBK+TWp4b7z+2g319Y8smTdvnldfX99r4PlORcBUt4UMzZgx46yxY0Ylc3kbbwtS8bECgac8tHUZPPnk3BRU0nPWzp7kDFS2OOIuCBjZHMDGuL10rXEsMzDgQgAJDSR0sri4AqNIHZDUFtpa9zFGAGNVzLJhrLsvMBEKZ2FCE+XrlylfpKo4zGdM6GS1A7IYZBhRwp+T69ZGIqD7VxCsNYDEYI+T+1JxBGCGIOhQsDki7TmDKXqWp7XYjo7wiOOOePma7189paklr8RGbsXiWygKIyAQgfvV+frttxdu+Y//uPw3IqLK7f7ubaccYOrUqQJAJk7c/2ylCIUgIGHHx8tNGWaR2uokLV65qe39dxe+Qun06cIsRGpXTs0eTYoBlVFEjWGEhciZEpVlAzu/PBHA8QsKOgrLYrhQDQUItVOy7xYUZF8xIQtpVdzZLDaqJcJfwui5Em8750FArivG2ZxkbbShtyu9CuIoR4RAQcLle1OEwMKlfsXQr+IoTKwYPBtjH0mb9EiHq25/LWxf1Up+zScBy6JYEYHFsKqpSi588L4/7y9euror38kE5bJAiiafeykRo6YqyUG+4D366GPfaWtra62vr9cAStTSre2QACKvoJ0yZcqQvcePP8IpwKJ6yTQCWJDKeHj55fnNYdu6NV7dWMtmh0EBe9Ccn90GBjAGEvnigVj2OkwcQjChdRPQ7ftMIawyQQgxoYMk2WXMxc+IHyShLQJT5Y3ixD82ZZZZ7BBgxHXrxRqAJYguSJR/A7mAP2c6cyyGLJColpSX1HbFL9bYzbf/GOlxN4q495DSrATKZJsWPPTMff2HDB44Zu3mdtZaRbRU7nMR+J6Cp8nWZnzvscf/8disWdfe2R306a3tkAAivNicffZ5548YMbxPLm+NiIuU6O6Y0IrQkWU89/yLK4D0vmxFR4j6HlkAcb2boowhICgQuOA8NsIaMetz8XTk6p1BwQRhlFxRpmCRzphsLmNCCzG2uP1Nj06RQMKwB8AV/eLYtwmdT9sCcQUPxNvZAO4awCtDt6ILxE22jX0iBGHLyq9RSbMV+eW3LgmbXkup5L4jwGGbUNBJnpf2dJUOm7ZsuePu3/snnHjMmHWbulhpUo7zqYqP8LRT/Ab2TdPS5Stbfv2rP17RG+jTW9shAUTsH5MPP/Q0rUhyhTxZcUkM5Ti1tVYymQytXLu98aVnn6+mdL9DI/av3FhQcUB3J4ffLcfIjocLoUZIIGOiAlbRIEbaNGmH2nI+AbFSfI7DzgXWWkHABGscjl6U87HVpUCwYBsUo3XdQyIk0QJkHH7vYkC5THHiKMWBnKYprCAKQhYQNYmUB7IKlo0WcvuGQxJWpwdq1f4G59b84S7TvuB6So19QCj8jSjaDlLskReGTdvW/faPP22/aMZ5B6/f1CqKPCUc4xTO5e6UPoeg19UkbT6X9R55+MGvPfvsI5vr6+t7gD67TQCR9m8POOCAEYMGDjrVCsiyC6Tq0USQzmg8//zc9qC9sa+uHZyyIcuHs/67NYocBqGFhCGJVk7piv3o5Pbcg4Wzx3t2DsLsgBhrIjOrW+ozKQDOR9yLExbWWLAJQWwgRlx8THxlVE6eAED3sHg0hNuEAC02xRwyqZTSiZSWhkc2Flbf9BsONv4BpLoE+U8CqfeV8kYonUDYuuGF2/7y+8LnP//ZU7c0dLFWWnVXMIkEWiloBaQS2lSltPfY4y/88fvf/e5dIuIR0W6lm/Yqp2Pt//LLP3/CuHHjUtmcsSKROwooepsEgPY8yuUMP/ro4zmo5Hi3+qm430A8KHuaGxjJZ8usKDSaKLBWwjzIGpANo5/GiYbQwhhTxrZLU8HMsHG9fBt74koHbAEShiBbuqlcFFlmWBMANgBx4AiheDjNniMXb9TxWJFoBxeep1TtaqIaS+lBSsuWgNf+8a78B9dcwpYWquRel0DYB3vfIaVSSimY1sYNv/z9L9Ofvfizp67b0iGhibhKN5WK4LJ8E55n+9cmvbfeevv9s8781Dd3R+6Xt145QKz97z/pkHN830NTRzby05QVYYQDU6qq0njn/ZVY8OrrIyiVYmb2KNrDFjFL76WVg0IVPoJ4ZYOE/IztXDP/DS8xfwIlx/c1JrSKO8mFvMRb1ElkiqXhNlZ2S5IiEJLZL5qOyvPgIpbib3ARPSQCKuMAKvbMKg2wBpnIAkDJPI3xLfcehjMjXFogQToFeqlQ4UEr+gjlk1YNz20wG+75RtD57hOjR19K67bOGyIwsyk9+mtaJfa3zDBtjS/f/+id9swzTp2yflObCIF0FJbv9oZzL9XkahtqgvTvk1ArV67K/ulPN52vlMrPmjWrW0TOHhLAzJkzlVLKjh07dvCQocM+ZdgZJc46K1OSAAhbSaUVzXvhlQ7T1VKra/pF4/RP8f+YHJh0uiHY/vb9pvHydGLvLxyXGHLGaTY1ApxtEkIBBFepQ4ThKbelKkpMIDbjxIaGxDpnDHElqy9W4XBKXEVz54upPEXO4JS/ciCEUVoXAiGqgsJxyh96CG99ehtyD/0st+kvNwNDAww+xVu3bm0BWLcWqVGv6WTtN01Xh8mk6MHnXn1i9KFHHn7E5q3tHBN55VxGX6UImkT696myTY2N+u9//9slt9566/I5c+bo6dOn7xH40hsHUCIin/3s504Zv/e46lzBWCLo0uZQpc54SqOjy+LJp57uAHk+oDPl+KBT/Lrf16MJIKJIkVKaWISYDROImEAq1SfB1jbkP/jFKm/jY88nR1wwXPodtq9NDwMsMaxlJazBQiLOJ1tE5dz4EaJoHLcpUynJpeQ3B9gEEOaiY9qlotoWFi0Q6eeYUkRh7BYDRYwOEumtEBZhIoFTTPy0yq2pvxd2+zWoOnIQ8o2HYNuaV4CVhgjwEslDwtaGtQceNHHlww/9dfKwUSPHrt3QIp7WitzWmM6CiF9NzuJSSqG2NmXEhv5999//zdmzZz8wd+5c78QTT9wtuV8x2d1PRKaDTDnxhNOSCQ+hMSJMFeHMgMt2zVSlaMWqdfmFb7xTRalq7cyweMIpygApRbIUV6db4KyJxNeayPOVMZaC1qYO09HZpbSn4Oh/GAtdC/BK1E38uclt+VXXBz841L4/8zK97rZ1fm6V0omUR7qagkIBNgwiyRNV7BIpCPR2uFUvzvkSRhme1pmQbMCmICbIQqBXOgTEcSGx2T8peFtgA0Q1XUrh4DAgG4iIZdHVTF6GAGoE02oId5AQBGEaybofqMSYb1G45W7S9htIBvck+k34psiwQ8L2FT/47ncvf3H+/CcOGTB05NgNWzrZ873I/mAQLBQpKHJVSDURtFKoqUqEmYTy//a3O276zy9/+VdvvfWW/2EmH+jJAUgpZdGvX+2QwYNPEwBhaHVvuiKzIJnReOyJZxKmc+sLXs2w0wyHZS5RRwRlDExEQUgp0tonNqxsrh026NqGZNX2gw+Z5F984Rldb7+zaMNdd9w9gaqGTPB8zQydJlG/krYNRwJb1mCmqMJsug9tr77rbXjoGH/oKcdSZp9zMPiwtLC1gColtErYRl5qPYEHaZCALQmbohx3vSSHz3sCIq+1HNUXlTqPuWugEwF5giSYmARQQjqtoZOkJE9edh3strc32fyW+7VKnSJC1ULWhdMLs2j1syJWr5MImtuOHzqq7gePPvqPqRP2nfTp9i6DzpZ28T2tyoJSiitGAXDZX0BNVTKsSWv/rrvuue+LX/ziVyOl70NNfg8CmDlzpp49e7b5wRX/edJeY/caWAhgIaR7s+F9X0tbR0gvz39lLuANZjY5AqWBMns++gKlFCmtyVohzmXBhcZ2XdN31QknHNZ0yWfO4alTjh85eOjQ4YmEXxMG5sDzzznltcu/dM32tubsEaounSLQJJvky1HAbzGbGJiWRb91203ztvvM2pvXKSSf1esP3JeRu4L8REaYNYgsxLbCSybg1YrINmE2TMwiEkdvWufWVCQETQzSKKm7gEruq/wkRGeYla+0X0WEBDyEoMIWhM1Lcty68K18x6JVNti2mJJ9TiDlDXQcjkiKOfEwOpHywmyu2Wa3PHLpFz476Kc/mfX9AYMHDd+wtZOFLWmliJl7xgJCoLTDO6oyibAmrf0HH3qk/rOfvWhGnNxRpJQP0SreFpsQDz380J3nnH3OZ5rbCyYfWK970gMzS6aqipat27h9ypEnvilQR0FTEqKrARIoDaUVEbmdsyWfFQSFRlVTtemoyft3XjT99OpPnnJSnyHDhw9OpBLpjk6grb0TQRhIMpGggf2r0drcsHX2j37eeOstd1fDSj+vumoVrFllbLgBzH9BuPG94kf4o28Q4CTy/MOjpABR8Ahh2xZ/+CnV1P/TNWIN4FUDugYQz+H5bEEcgIWQqBsBu+q6dwtbnjgAiT5EIJJC84raideOtX0+4XHrwqy22zZwfuti27Fhqe149VmTX7sGgEVi9IE6kUixtbfB031FSr5SrTyYAEC+efXEA8a//5fbflk36cCDT8gVgLb2nCWtNIlFBcZCVOSfWmtoBdRUp8KalPLr6++fM336BfHkl+zsj4AAIvNdqt94881Vh0+ePGhrc5cYU5lnBgDGGOk3oI5+9qvbWq7//rVpr65/yogVRURKaRjRQDZgBJ2Nui7VcuKxk7d8bsZ5yaOPOXzo4GFDB3t+It2VM+jK5mANsUCiOhACkIK1hvtUZ1RNtY/3F727/Eezf77skUeePQiUGqUzKRCZx0xoFgLkgaQRIleA/PEg2KjUKQCxBFgJzQaE2TspPTSnEv0PUsmBo6hq/AaV6QPY/Eix6CuiQuWpRLDlsTabX3M4VJpARMTcpr0Bz5Ikqjm/Yb01a24HsBBAVFg58hMkxuxDxDeK9s8hpQwprZT2lAm6OtDV3jxoxMiW3//i2q2nfepTR3iJZL+tDe1iGNA7irUnglaRwkckfWoznE5A31d//y3Tp19wRRlH/qcmv4IAYhPim9/85pT/njlzXrqqjlvacio0keOirClSyFQn5ZNnfZ4WvPwK/JoMCiGAbCdgg4Zk3wENU489NPv5S841Rx5x6IihQ4cPVL5KdnYy2ruyCGwgCgpKabeTWjcrgQiwrqguDR5Qh7SH7Pz5r67/5W9uWfHQ/U/4AJ2KqlqlFYHZgDhkIc8pGE5fifQobhWRM1FY9SrUkPNhc21A22sAuqJXTQSQAzAMSB1Dyf6Xik5OcgaI5wxEk3tACu2/QKJ2kEbK2r6HP49tf+8qdjY9cphivZSUXwtPCSmfTGCAXEfD8DFDn/7RzKurTz3txKkDBw+pa2jKI1cosKdjWd87PK4UIeFpaEXcr09KTJDXDz/y2I8vunDatSKiPoqVXxzr+JcYPqy///7/veD8869u7gxMPh96rswpF68WEVRVZbBq41ZzwjGnbArb2ocC2JzqW7P8+GMO7fsfX7hIJk8+eFS/AQMHJdMplcu6SuShMSwAKVJUjvy4KJvKOrtxDDyJwIRG/GSCBvbLgK0J169bu/jXv7tlw1/ueHBQ0N40GF6mn0pl6pTSEGFhcRvviYgIDGDsIqjwLOQ2bSy9QAHjvprE5n+cAvR9C7kFW5Hc+1giczeA4SAiIU0EWBIRFrkIuZX3ITHyBwQaKVqWAVitlNpbwf9P0mrvMAgLyBcIoOX7HTqp7ZtXfi55xqdO26v/wP79mtoN2ts72VfKbUQYI6RSAneiYFRo5Vi+75Ed1DetN2/ejNtvv+Paa675wY+jOfpno2x2SABERIlXX3998ZFHHLH31uYsG8MqrncfN8Mi/frX4ve33L38+u9++x8XffaC8WedeVr/w484fGhtn77D/aTndWYtOju6wFZYKUXUC5nvbC+k0uWRf54I1rIorWlgv2poArZt27rh6WeeXXnLLXe3vfnm4sNhwzokqquRSsPXCsQWRgIWaxU4XCcit4ERABQi8G8FVrYXX5gYPYFIPQftDRMIE5GKN6qNihhsBuElRXIcKT0cKkHCAhsEQJC1gG2oHTCoaca007d8/tKLBkyatN+4ZCpd09oRorMr59RM92GVaKqUvpe0wNMaCkA65Zs+1b737qL3Gu69p/6zP/3pdc9E+tk/pfD11ggApk2bpuvr6+1ll33pxBt/fuPzmeo6bm7LKmsZbMu5jYNolQdavXrVqol7j/b69OkzSilQe7tFR7YLIYfiwYdSMSzXOxxcJACiKO8ekSOwHBeufLeL+bSsQKq2thbVGaCjrTNctXrVln88+XzTU8/Nb3z9naUDbGvbcAB18JNJuEjWktdOBCRmi0C2k2CbKF4uTOcC3gjS2gDkORsgVsScH8GyQMLA5ZkhXAtg2cixY7LnnHWKPvnkE0YfeshBowcPGVprBaq5JYtCIRRXjYRIpJeYfcClxUXnlKeQ0LB1tRmV0KDnnnv+ua9f+bWvLF26dMWe4vt70ggA5spc7yR1kvnrX//200su+ez3mtsLNluwngljbkNRAEoEspOgtqYWxjC6sllYa115nWixUxHC7B4SEOm2IpAoC8i5M0t5RCW4NbbTY9Qu/r/7zTJErEjCT6ja2jQSCcCGYba5sanp/aUfbH/xpVcL8994l5csXe41bW9KolCoA8Jm50JRh8SpOyCvyIWhPSBGe4zZFmVP5wDVqjMJjBo9rOuwg/cPzjvrtMb995s4btTo0cOS6aqBSgOdWYuOziysMCtSxRXvuD0Xv9+9ViJnjnKsQUQy6QT3rUnoVWvXyjNPP339V664YiYA/jDw7p40p0o69k/zXnhh0ZQTTth/W1MXF0IoLlYKVWUUHE2AtULQpR1L4wdS9wmPsPfi+bjQdpRpKCx+QlutFQVhCAG0SBSKVfQollrp36rklYycFJ7vUyaTRCajoQjggil0dbZ3tra2Na9dt6HQ3NK8pZArrHnx1bf26+rKb2luaurf2VWoC0yoIaQ8TyOTSSXravtk9x0/cnVdbfXwsWPHyvi9xyTraqsHDRgwqEYnEjXK85QxQFc2QDZXAESYtCLqrs1G/SUpGxcVV+wGNAhJ3zN965IeBHjzzbde+P2fbvrB3/7851ejgA6KQvM/tubFkb+XXHLJhPHj9903MBBrpYfpVyzAEIdTq2gF9dLKChgBUciyirDs2HzxFHE66aO2SmsAXj60SNUmkA2YO7sCMIlyCbNSDAOLYkBQHvhD5PriYHqLjs6stHeIiIAS2k+mUrXJAcP79h86ciw8jUlEwAUzpoOtZc/3AnGRxeIgXgIAT0R8pfUhSiPaHRQIQ0E2FyLXmYUVywQFRYqcKUdF06O3sVBExU25SDFAcOFb1RmV8eEtWfJB1/PPz73uyiv/83+iMY5Z/kcq73tr3rCzhmnMhhx59NHnDRs62GvrDAxAnltZJWWsQnb1ar5WuIEibu8KWDpbT0Rr4mTCR02VpzWgw9Bg+fLlW5a+v/TZRYvff/qoo444/+hjjj5vUN8qtGeNzedDMkxuI6bYgSNUpjXGhFbqg45tDAIsG3R2GbEdDBGWUgClAyeVUqlIAwEgRWJjFhTzawkS26rOYaWgQFGARnfULgaSKVI3JCJ6BXJhW/CUslWZpKpOK71p02Y8/daCv177w9k3LF684AMRUdOnT6ePS9731iiyK/mZuc++cPLUT5zQ0NxlCyG0sZXstzTpvcCVZX5/ij7YEbyIpxSnUj6qMlprAPnAYvXy5c1r1q1+4YMPPqj/9re//TiAokZ+zTXXXHTGmWdeM/mII/f3FaE9a222UKBoZ7UopV66rY3exUR5AGs5nF3cG1MELD2/RUQqrZQy7heRUO+DGcn2UsRStCyIJOF7XFOVoowP1dDQhMWL3nvkrnvu/Pmtt976UvTsj03R21nziIiPP/74kWPHjDucAbEWqjf2312LLQm8iLWrookjCUWcSiWoukopBejACpYuXZZbvXrVSx+8v/SO733v288B2Bo/X0T0rFmzaNasWUxEd19//fUP/Ox/f/Ufxxx9xDcPO2zyXrWZDLIGks3mXNUyidWo6P4yKVnpt6BykVX8e++EjW7nSqFjlcGi5RyndD0RKscHAiLhRDLBtTVJL0HQDY2NeO2995996MGHfvbb3/7q6bJvl3/F5APOGUSnfvKTZ44eNSqdK7ARIo+LoVzdPVORuItsNlJO2xdh8bTidDqJ6rTSGtDZXAFLl6xqXbFy1YuLFy1+6JZb/vjC+vXrVxefJqLr6+sxffp0jj9+9uzZmDNnjp4xY0bhO9+66ncAbv/xj398wTHHHHPFfvtPOmrwoIEaANpzlnP5UKwxikgRaxUVbyw3IUu/lnwy5Wd33bpfGYkElEQiFdm9UpHZSBBPE6dTKapJQwFQK1as7Fq9ds09Dz/44J033XTT3KhPatasWfhXTXzcCAAef+LJhz51+mnnNLbnTSGwnjFS3EIWcB8HOEVGqZgAhH3P52TCU9UZpTSAzlwBy1csa1y/evXzS5a8d+811/z3ywC2xy8TEVVfX0/Tp0/fFaBBc+bMUTNmzLCxAnjFFVcce9wJU7544AEHnrb3+PFDMykXet+ZszYXhGJCVsJuF7944qxwUS5X5jOUc4HeCaKCSxR/ISjtoo2LrguQKE3s+0pSyaSqSTosurG5BatXr168aOGiu2688af3rlixYnX0XJo+vV7V1398pt2eNG/QoEGDhw0behwAWBNqEWerKtIlxEojVuY46RGnUklVlVIKgGrr6MKid1dtW7ly9WvvLnr37ut/NOt5AA3xCyJKd5XyiXbXpJHI9qWZM2fqWbNmWSKa/6c//Wk+gNofXX/9qQcfuP+l48bve/SY0WP7D6xLAQAKIZAP2FprxBhLxb2XQCRCKCZ+SCwOULTJy/MJYgZIZWLDXUIAWJQS0Vpzyvcpk/J0Qrui2q3tHXh7yYqNq1auePbZZ5+/9+abb34Wbi9YiIiePn16vOL/LSYfAOirX//6p6+bfd19VTW13NaRV6EpxZxHdjp7nuJUOqVrku5US2sb1q5ds2rTxo2Pv/HGG09cd911rwFoA9wgMXORveMjMmWmTZum58yZA6WULVudg374w5lTDzhgv1NGjRx1/PARI/YePnKELg9fyVsgCG2UWuhSuIXjmCSO4b54OKKfRc4nWinRWsHTmrSnVMp3kWAAEBjBhnVrC9sbti/YtHnTy2++/vrzN95443wAne5+heeee86bN28ef9z2/IdtdNddd99y0UUzvtjWFVpjRLNlsAh7CY8zKV+nfTcq2xoasWXr5sXLlrz/0iuvvPbgb37zm5fhPGnxpO8ue/+n+zxnzhw1bdo06cZR9EUXnT9+8uSjjx41aswhgwYNmlDbt9/eNdXVo/r3669ra2uKJYg+TMsVAjQ2NqKltXXbli1btzQ1Nb25fOXylx+4996X33vvvaJu83EtgI+r0euvv7Fx8uTDhrd25lkrjUwmqfxoCW3bth3r169fumzF8ifeeO3lB37725teRSnKB9Zar76+Xv6FHxoTA7TWRX2hrCVOOOHk0YcdcfC4saNGjR46dOjIurq6GiM8rrq6pjYoFEZUZTISY3jsdt6SIAypK5vlbDa3Osh1rW1tbtm8ZcvWDxYtWrzmkUfuXwKgo/wlIkLz5s3TDQ0N/8qx+HBtzZo1Ut42bNwk8199bdG999476ytf+Y/DUZboqJSCiOg5c+bE8bD/To1mzpypZs6c6YmIJyIqNv120vQujp4vIYLWGnNFvDlz5uiZM2d+BEmw/7pGN91004tTp049vrm5ecPSpUv/MX/+C3f95S9/exVAAABKaVhrvOnTp0t9ff3/W9TtiIL2339/GjhwINXU1NBhhx1WtHE9z3PZgN01/kghNMZoADRv3jzMmzcPS5YsiccA+H9rHHbY/j+eSSm79D30iQAAAABJRU5ErkJggg==";

const isCapacitor = () =>
  typeof window !== "undefined" &&
  !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());

// ── Feature Showcase Panel ────────────────────────────────────────────────────
function FeatureShowcase() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef(null);

  const features = [
    {
      tagText: "#60a5fa",
      title: "Divulgue em centenas\nde grupos automaticamente",
      desc: "Publique sua mensagem em dezenas de grupos de uma vez, com intervalo inteligente.",
      component: "autopost",
    },
    {
      tagText: "#4ade80",
      title: "Atenda clientes 24/7\nno automático",
      desc: "Configure respostas automáticas inteligentes e boas-vindas sem parar — mesmo dormindo.",
      component: "whatsapp",
    },
    {
      tagText: "#a78bfa",
      title: "Inteligência artificial\nnos seus anúncios",
      desc: "Analise performance, identifique oportunidades e receba sugestões de otimização com IA.",
      component: "metaads",
    },
    {
      tagText: "#fbbf24",
      title: "Publique no horário\ncerto, no piloto automático",
      desc: "Agende campanhas para rodar automaticamente no melhor momento para o seu público.",
      component: "scheduler",
    },
  ];

  const goTo = (idx) => {
    if (idx === current) return;
    setExiting(true);
    setTimeout(() => {
      setCurrent(idx);
      setExiting(false);
    }, 400);
  };

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setExiting(true);
      setTimeout(() => {
        setCurrent((c) => (c + 1) % features.length);
        setExiting(false);
      }, 400);
    }, 5200);
    return () => clearInterval(timerRef.current);
  }, []);

  const f = features[current];

  return (
    <div style={{
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Grid background */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: "linear-gradient(rgba(59,130,246,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.06) 1px, transparent 1px)",
        backgroundSize: "40px 40px",
        pointerEvents: "none",
      }} />
      {/* Glow orbs */}
      <div style={{ position: "absolute", top: -80, left: -80, width: 320, height: 320, borderRadius: "50%", background: "rgba(59,130,246,0.18)", filter: "blur(90px)", pointerEvents: "none", animation: "orbPulse 6s ease-in-out infinite alternate" }} />
      <div style={{ position: "absolute", bottom: -60, right: "20%", width: 260, height: 260, borderRadius: "50%", background: "rgba(139,92,246,0.12)", filter: "blur(80px)", pointerEvents: "none", animation: "orbPulse 8s ease-in-out infinite alternate-reverse" }} />
      <div style={{ position: "absolute", top: "40%", right: "5%", width: 180, height: 180, borderRadius: "50%", background: "rgba(59,130,246,0.08)", filter: "blur(60px)", pointerEvents: "none" }} />

      <style>{`
        @keyframes orbPulse { from { opacity: 0.6; transform: scale(1); } to { opacity: 1; transform: scale(1.12); } }
        @keyframes apbBlink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes apbDot { 0%,80%,100%{opacity:0.3} 40%{opacity:1} }
      `}</style>

      {/* Logo (clicável → volta pra landing) */}
      <div
        onClick={() => navigate("/")}
        role="link"
        tabIndex={0}
        aria-label="Ir para a página inicial"
        title="Voltar para a página inicial"
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/"); } }}
        style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 48, position: "relative", zIndex: 2, cursor: "pointer" }}
      >
        {/* Logo wrapper with glow ring */}
        <div style={{
          position: "relative",
          width: 58, height: 58,
          borderRadius: 16,
          background: "linear-gradient(135deg, rgba(59,130,246,0.25), rgba(99,102,241,0.15))",
          border: "1.5px solid rgba(59,130,246,0.35)",
          boxShadow: "0 0 24px rgba(59,130,246,0.35), 0 0 48px rgba(59,130,246,0.12), inset 0 1px 0 rgba(255,255,255,0.1)",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <img src={LOGO_SRC} alt="AutoPost BR" style={{ width: 42, height: 42, objectFit: "contain", borderRadius: 8 }} />
          {/* Glow pulse ring */}
          <div style={{
            position: "absolute", inset: -4,
            borderRadius: 20,
            border: "1px solid rgba(59,130,246,0.2)",
            animation: "orbPulse 3s ease-in-out infinite alternate",
          }} />
        </div>
        {/* Name + tagline */}
        <div>
          <div style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 700, fontSize: 22, color: "#fff",
            letterSpacing: "-0.6px", lineHeight: 1.1,
            textShadow: "0 0 30px rgba(59,130,246,0.5)",
          }}>AutoPost BR</div>
          <div style={{
            fontSize: 11, color: "rgba(59,130,246,0.8)",
            letterSpacing: "1.2px", textTransform: "uppercase",
            fontWeight: 600, marginTop: 3,
          }}>Automação inteligente</div>
        </div>
      </div>

      {/* Slide content */}
      <div style={{
        flex: 1,
        position: "relative",
        zIndex: 2,
        opacity: exiting ? 0 : 1,
        transform: exiting ? "translateY(-14px)" : "translateY(0)",
        transition: "opacity 0.45s cubic-bezier(0.4,0,0.2,1), transform 0.45s cubic-bezier(0.4,0,0.2,1)",
        display: "flex",
        flexDirection: "column",
      }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          padding: "4px 11px", borderRadius: 20,
          background: f.tagColor, color: f.tagText,
          fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", textTransform: "uppercase",
          marginBottom: 16, width: "fit-content",
        }}>{f.tag}</div>

        <div style={{
          fontFamily: "'Space Grotesk', sans-serif", fontSize: 26, fontWeight: 700,
          color: "#fff", lineHeight: 1.22, marginBottom: 10, letterSpacing: "-0.5px",
          whiteSpace: "pre-line",
        }}>{f.title}</div>

        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.48)", lineHeight: 1.65, marginBottom: 26 }}>
          {f.desc}
        </div>

        {/* Mockup window */}
        <div style={{
          background: "rgba(0,0,0,0.48)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 10,
          overflow: "hidden",
          fontFamily: "ui-monospace, monospace",
          fontSize: 11,
          flex: 1,
          minHeight: 0,
        }}>
          <div style={{ background: "rgba(0,0,0,0.4)", borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "8px 12px", display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#ff5f57" }} />
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#ffbd2e" }} />
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#28ca40" }} />
            <span style={{ marginLeft: 8, color: "rgba(255,255,255,0.2)", fontSize: 10 }}>autopostbr.app</span>
          </div>
          {f.component === "autopost" && <AutopostMockup key="autopost" />}
          {f.component === "whatsapp" && <WhatsappMockup key="whatsapp" />}
          {f.component === "metaads" && <MetaAdsMockup key="metaads" />}
          {f.component === "scheduler" && <SchedulerMockup key="scheduler" />}
        </div>
      </div>

      {/* Dots nav */}
      <div style={{ display: "flex", gap: 6, marginTop: 28, position: "relative", zIndex: 2 }}>
        {features.map((_, i) => (
          <div
            key={i}
            onClick={() => goTo(i)}
            style={{
              width: i === current ? 22 : 6, height: 6, borderRadius: 3,
              background: i === current ? "#3b82f6" : "rgba(255,255,255,0.2)",
              cursor: "pointer", transition: "all 0.35s",
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ── AutoPost Mockup ─────────────────────────────────────────────────────────
function AutopostMockup() {
  const fullText = "🚀 Promoção imperdível! 50% OFF hoje. Acesse o link na bio! 🔥";
  const groups = [
    { name: "Vendas SP Centro", members: "12.4k" },
    { name: "Promoções Brasil", members: "89.2k" },
    { name: "Negócios & Empr.", members: "34.1k" },
    { name: "Ofertas do Dia", members: "21.7k" },
    { name: "Marketplace Geral", members: "56.8k" },
  ];
  const [typed, setTyped] = useState("");
  const [selIdx, setSelIdx] = useState([]);
  const [sentIdx, setSentIdx] = useState([]);
  const [phase, setPhase] = useState("idle");
  const t = useRef(null);

  useEffect(() => {
    const clear = () => clearTimeout(t.current);
    if (phase === "idle") { t.current = setTimeout(() => setPhase("typing"), 600); }
    else if (phase === "typing") {
      if (typed.length < fullText.length) {
        t.current = setTimeout(() => setTyped(fullText.slice(0, typed.length + 1)), 32);
      } else { t.current = setTimeout(() => setPhase("selecting"), 400); }
    } else if (phase === "selecting") {
      if (selIdx.length < groups.length) {
        t.current = setTimeout(() => setSelIdx((p) => [...p, p.length]), 270);
      } else { t.current = setTimeout(() => setPhase("sending"), 500); }
    } else if (phase === "sending") {
      if (sentIdx.length < groups.length) {
        t.current = setTimeout(() => setSentIdx((p) => [...p, p.length]), 310);
      } else { t.current = setTimeout(() => setPhase("done"), 400); }
    } else if (phase === "done") {
      t.current = setTimeout(() => { setTyped(""); setSelIdx([]); setSentIdx([]); setPhase("idle"); }, 2800);
    }
    return clear;
  }, [phase, typed, selIdx, sentIdx]);

  return (
    <div style={{ padding: "14px 16px", height: "calc(100% - 32px)", overflow: "hidden" }}>
      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 7 }}>Nova publicação</div>
      <div style={{
        background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 5, padding: "7px 8px", minHeight: 42, color: "rgba(255,255,255,0.85)",
        lineHeight: 1.5, fontSize: 10, marginBottom: 8,
      }}>
        {typed || <span style={{ color: "rgba(255,255,255,0.22)" }}>Digite sua mensagem...</span>}
        {phase === "typing" && <span style={{ display: "inline-block", width: 1.5, height: 10, background: "#3b82f6", marginLeft: 1, animation: "apbBlink 0.7s infinite", verticalAlign: "middle" }} />}
      </div>
      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", marginBottom: 5 }}>Grupos ({selIdx.length}/{groups.length})</div>
      {groups.map((g, i) => {
        const sel = selIdx.includes(i);
        const sent = sentIdx.includes(i) && (phase === "sending" || phase === "done");
        return (
          <div key={i} style={{
            display: "flex", alignItems: "center", gap: 5,
            padding: "3px 6px", borderRadius: 4, marginBottom: 2,
            background: sel ? "rgba(37,99,235,0.12)" : "rgba(255,255,255,0.02)",
            border: `1px solid ${sel ? "rgba(37,99,235,0.35)" : "rgba(255,255,255,0.05)"}`,
            transition: "all 0.25s",
          }}>
            <div style={{
              width: 11, height: 11, borderRadius: 2, flexShrink: 0,
              border: `1.5px solid ${sel ? "#3b82f6" : "rgba(255,255,255,0.15)"}`,
              background: sel ? "#3b82f6" : "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 7, color: "white",
            }}>{sel ? "✓" : ""}</div>
            <span style={{ flex: 1, color: sel ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.35)", fontSize: 10 }}>{g.name}</span>
            {sent
              ? <span style={{ fontSize: 9, color: "#4ade80" }}>✓ enviado</span>
              : <span style={{ fontSize: 9, color: "rgba(255,255,255,0.2)" }}>{g.members}</span>}
          </div>
        );
      })}
      {phase === "done" && (
        <div style={{ background: "rgba(22,163,74,0.2)", border: "1px solid rgba(22,163,74,0.4)", borderRadius: 5, padding: "5px 10px", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, marginTop: 5 }}>
          <span>✅</span>
          <span style={{ color: "#4ade80", fontWeight: 600, fontSize: 10 }}>Publicado em {groups.length} grupos!</span>
        </div>
      )}
    </div>
  );
}

// ── WhatsApp Mockup ─────────────────────────────────────────────────────────
function WhatsappMockup() {
  const script = [
    { type: "user", text: "Oi! Qual o preço do produto?", delay: 0 },
    { type: "bot", text: "typing", delay: 700 },
    { type: "bot", text: "Olá! 😊 O produto custa R$97,00. Quer mais info?", delay: 1500 },
    { type: "user", text: "Como funciona o frete?", delay: 2700 },
    { type: "bot", text: "typing", delay: 3400 },
    { type: "bot", text: "Frete grátis para todo Brasil! 🚚 3-5 dias úteis.", delay: 4300 },
    { type: "user", text: "Perfeito! Quero comprar!", delay: 5600 },
    { type: "bot", text: "typing", delay: 6200 },
    { type: "bot", text: "Ótimo! 🎉 link.autopost.com/comprar", delay: 7000 },
  ];
  const [msgs, setMsgs] = useState([]);
  const timers = useRef([]);

  useEffect(() => {
    const run = () => {
      setMsgs([]);
      script.forEach((s) => {
        const t = setTimeout(() => {
          setMsgs((prev) => {
            const next = prev.filter((m) => m.type !== "typing");
            return s.text === "typing" ? [...next, { type: "typing", id: Date.now() }] : [...next, s];
          });
        }, s.delay);
        timers.current.push(t);
      });
      const reset = setTimeout(() => { timers.current.forEach(clearTimeout); timers.current = []; run(); }, 10500);
      timers.current.push(reset);
    };
    run();
    return () => timers.current.forEach(clearTimeout);
  }, []);

  return (
    <div style={{ height: "calc(100% - 32px)", display: "flex" }}>
      <div style={{ width: 36, background: "rgba(0,0,0,0.25)", borderRight: "1px solid rgba(255,255,255,0.05)", display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 10, gap: 12 }}>
        <div style={{ width: 24, height: 24, borderRadius: 4, background: "rgba(34,197,94,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>💬</div>
        <div style={{ width: 24, height: 24, borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>📊</div>
      </div>
      <div style={{ flex: 1, padding: "10px 12px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
        {msgs.map((m, i) => (
          m.type === "typing"
            ? <div key={m.id} style={{ alignSelf: "flex-start", background: "rgba(255,255,255,0.08)", borderRadius: 10, padding: "6px 12px", display: "flex", gap: 3 }}>
                {[0,1,2].map((j) => <span key={j} style={{ width: 5, height: 5, borderRadius: "50%", background: "rgba(255,255,255,0.5)", display: "inline-block", animation: `apbDot 1.2s infinite ${j * 0.2}s` }} />)}
              </div>
            : <div key={i} style={{
                alignSelf: m.type === "user" ? "flex-end" : "flex-start",
                background: m.type === "user" ? "#2563eb" : "rgba(255,255,255,0.08)",
                color: "rgba(255,255,255,0.9)", borderRadius: 10, padding: "5px 10px",
                fontSize: 10, lineHeight: 1.4, maxWidth: "82%",
              }}>{m.text}</div>
        ))}
      </div>
    </div>
  );
}

// ── Meta ADS Mockup ─────────────────────────────────────────────────────────
function MetaAdsMockup() {
  const insights = [
    "Seu anúncio 'Promoção Verão' está com CTR acima da média do setor.",
    "Recomendo aumentar o orçamento em 20% no público 25-34 anos.",
    "O criativo com vídeo performa 3x melhor que imagem estática.",
    "Melhor horário detectado: 19h-21h nos dias úteis.",
  ];
  const [idx, setIdx] = useState(0);
  const [chars, setChars] = useState(0);
  const t = useRef(null);

  useEffect(() => {
    const cur = insights[idx % insights.length];
    if (chars <= cur.length) {
      t.current = setTimeout(() => setChars((c) => c + 1), 30);
    } else {
      t.current = setTimeout(() => { setIdx((i) => i + 1); setChars(0); }, 2400);
    }
    return () => clearTimeout(t.current);
  }, [chars, idx]);

  const cur = insights[idx % insights.length];
  return (
    <div style={{ padding: "14px 16px", height: "calc(100% - 32px)", overflow: "hidden" }}>
      <div style={{ display: "flex", gap: 7, marginBottom: 10 }}>
        {[["Alcance", "142K", "#60a5fa"], ["ROAS", "4.2x", "#4ade80"], ["CPM", "R$8,40", "#fbbf24"]].map(([label, val, color]) => (
          <div key={label} style={{ flex: 1, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 6, padding: "7px 8px" }}>
            <div style={{ fontSize: 9, color: "rgba(255,255,255,0.4)", marginBottom: 3 }}>{label}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color, fontFamily: "'Space Grotesk', sans-serif" }}>{val}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", background: "rgba(139,92,246,0.2)", border: "1px solid rgba(139,92,246,0.3)", borderRadius: 12, fontSize: 9, color: "#a78bfa", fontWeight: 600, marginBottom: 8 }}>✨ Análise da IA</div>
      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.7)", lineHeight: 1.6, minHeight: 40 }}>{cur.slice(0, chars)}<span style={{ display: "inline-block", width: 1.5, height: 10, background: "#a78bfa", marginLeft: 1, animation: "apbBlink 0.7s infinite", verticalAlign: "middle" }} /></div>
    </div>
  );
}

// ── Scheduler Mockup ─────────────────────────────────────────────────────────
function SchedulerMockup() {
  const [prog, setProg] = useState(60);
  useEffect(() => {
    const iv = setInterval(() => setProg((p) => (p < 94 ? p + 0.4 : p)), 70);
    return () => clearInterval(iv);
  }, []);
  const campaigns = [
    { name: "Promoção Black Friday", sub: "hoje às 18:00 • 47 grupos", prog: 0, status: "Aguard.", color: "#fbbf24" },
    { name: "Oferta do dia", sub: "rodando agora • 23 grupos", prog, status: "Ativo", color: "#4ade80", pulse: true },
    { name: "Newsletter semanal", sub: "sex 09:00 • 89 grupos", prog: 0, status: "Fila", color: "rgba(255,255,255,0.35)" },
  ];
  return (
    <div style={{ padding: "14px 16px", height: "calc(100% - 32px)", overflow: "hidden" }}>
      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 8 }}>Campanhas agendadas</div>
      {campaigns.map((c, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", background: "rgba(255,255,255,0.03)", borderRadius: 6, marginBottom: 4, fontSize: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: c.color, flexShrink: 0, ...(c.pulse ? { animation: "apbBlink 1s infinite" } : {}) }} />
          <div style={{ flex: 1 }}>
            <div style={{ color: "rgba(255,255,255,0.7)" }}>{c.name}</div>
            <div style={{ color: "rgba(255,255,255,0.35)", fontSize: 9 }}>{c.sub}</div>
            <div style={{ height: 4, background: "rgba(255,255,255,0.1)", borderRadius: 2, overflow: "hidden", marginTop: 2 }}>
              <div style={{ height: "100%", background: c.color, borderRadius: 2, width: `${c.prog}%`, transition: "width 0.3s" }} />
            </div>
          </div>
          <div style={{ padding: "2px 7px", borderRadius: 10, fontSize: 9, fontWeight: 600, background: `${c.color}22`, color: c.color }}>{c.status}</div>
        </div>
      ))}
    </div>
  );
}

// ── Main LoginPage ────────────────────────────────────────────────────────────
export default function LoginPage() {
  const { user, login, register, verifyEmail, resendVerification, forgotPassword, resetPassword, googleLogin } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refCode = params.get("ref");
  const redirectTo = params.get("redirect") || "/home";
  const [mode, setMode] = useState(refCode ? "register" : "login");
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    if (params.get("reason") === "session_replaced") {
      toast.error("Sua conta foi acessada em outro dispositivo/navegador, então essa sessão foi encerrada.", { duration: 8000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  // ── Verificação de e-mail por código ────────────────────────────
  const [verifyEmailPending, setVerifyEmailPending] = useState(""); // "" ou o e-mail aguardando código
  const [codigo, setCodigo] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  // ── Esqueci minha senha ──────────────────────────────────────────
  const [showForgot, setShowForgot] = useState(false);
  const [forgotStep, setForgotStep] = useState("email"); // "email" | "code" | "newpass"
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotCodigo, setForgotCodigo] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (refCode) sessionStorage.setItem("autopost_ref", refCode);
  }, [refCode]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    const initGoogle = () => {
      if (!window.google) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCallback,
        auto_select: false,
        cancel_on_tap_outside: true,
        ux_mode: "popup",
      });
      if (!isCapacitor()) {
        const el = document.getElementById("google-btn");
        if (el) {
          window.google.accounts.id.renderButton(el, {
            theme: "filled_black", size: "large", width: "100%",
            text: "continue_with", locale: "pt-BR",
          });
        }
      }
    };
    if (window.google) { initGoogle(); }
    else {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true; script.defer = true;
      script.onload = initGoogle;
      document.head.appendChild(script);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (user) return <Navigate to="/home" replace />;

  const handleGoogleCallback = async (response) => {
    setBusy(true);
    try {
      await googleLogin(response.credential);
      toast.success("Bem-vindo!");
      navigate(redirectTo);
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao entrar com Google"));
    } finally { setBusy(false); }
  };

  const handleGoogleNative = () => {
    if (!window.google) { toast.error("Google não disponível. Tente pelo e-mail."); return; }
    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        toast.error("Não foi possível abrir o Google. Tente pelo e-mail.");
      }
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Bem-vindo de volta!");
        navigate(redirectTo);
      } else {
        if (!form.name.trim()) throw new Error("Informe seu nome");
        if (form.password.length < 6) throw new Error("Senha mínima de 6 caracteres");
        if (!acceptedTerms) throw new Error("Você precisa aceitar os Termos de Uso e a Política de Privacidade");
        const ref = refCode || sessionStorage.getItem("autopost_ref") || undefined;
        const result = await register(form.name, form.email, form.password, ref);
        sessionStorage.removeItem("autopost_ref");
        if (result?.email_verification_required) {
          setVerifyEmailPending(form.email.toLowerCase());
          setResendCooldown(60);
          toast.success("Enviamos um código de 6 dígitos pro seu e-mail!");
        } else {
          navigate(redirectTo);
        }
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail.startsWith("email_not_verified")) {
        // Login bloqueado por e-mail ainda não confirmado — o backend já
        // reenviou um código novo sozinho, só precisa mostrar a telinha.
        setVerifyEmailPending(form.email.toLowerCase());
        setResendCooldown(60);
        toast.info("Confirme seu e-mail — mandamos um novo código.");
      } else {
        toast.error(getErrorMessage(err, "Erro ao autenticar"));
      }
    } finally { setBusy(false); }
  };

  const submitCodigo = async (e) => {
    e.preventDefault();
    if (codigo.trim().length !== 6) { toast.error("Digite os 6 dígitos do código."); return; }
    setBusy(true);
    try {
      await verifyEmail(verifyEmailPending, codigo.trim());
      toast.success("E-mail confirmado! Bem-vindo(a) 🎉");
      navigate(redirectTo);
    } catch (err) {
      toast.error(getErrorMessage(err, "Código inválido"));
    } finally { setBusy(false); }
  };

  const handleResendCodigo = async () => {
    if (resendCooldown > 0) return;
    setBusy(true);
    try {
      await resendVerification(verifyEmailPending);
      toast.success("Novo código enviado!");
      setResendCooldown(60);
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao reenviar código"));
    } finally { setBusy(false); }
  };

  const closeForgotFlow = () => {
    setShowForgot(false);
    setForgotStep("email");
    setForgotEmail("");
    setForgotCodigo("");
    setNewPassword("");
    setConfirmNewPassword("");
  };

  const submitForgotEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await forgotPassword(forgotEmail.trim().toLowerCase());
      toast.success("Enviamos um código de 6 dígitos pro seu e-mail!");
      setForgotStep("code");
      setResendCooldown(60);
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao enviar código"));
    } finally { setBusy(false); }
  };

  const submitForgotCode = (e) => {
    e.preventDefault();
    if (forgotCodigo.trim().length !== 6) { toast.error("Digite os 6 dígitos do código."); return; }
    setForgotStep("newpass");
  };

  const handleResendForgotCode = async () => {
    if (resendCooldown > 0) return;
    setBusy(true);
    try {
      await forgotPassword(forgotEmail.trim().toLowerCase());
      toast.success("Novo código enviado!");
      setResendCooldown(60);
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao reenviar código"));
    } finally { setBusy(false); }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error("Senha mínima de 6 caracteres"); return; }
    if (newPassword !== confirmNewPassword) { toast.error("As senhas não conferem"); return; }
    setBusy(true);
    try {
      await resetPassword(forgotEmail.trim().toLowerCase(), forgotCodigo.trim(), newPassword);
      toast.success("Senha redefinida! Bem-vindo(a) de volta 🎉");
      navigate(redirectTo);
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao redefinir senha"));
    } finally { setBusy(false); }
  };

  // ── Shared dark input style
  const inputStyle = {
    width: "100%",
    height: 42,
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 10,
    color: "rgba(255,255,255,0.85)",
    fontSize: 13,
    padding: "0 14px",
    fontFamily: "'Space Grotesk', sans-serif",
    outline: "none",
    transition: "border-color 0.2s, background 0.2s",
    boxSizing: "border-box",
  };
  const passwordInputStyle = { ...inputStyle, paddingRight: 40 };
  const eyeToggleStyle = {
    position: "absolute",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    padding: 4,
    margin: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "rgba(100,110,130,0.9)",
    cursor: "pointer",
  };
  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "stretch",
      background: "linear-gradient(135deg, #0d1530 0%, #0a1020 50%, #0e1835 100%)",
      fontFamily: "'Space Grotesk', sans-serif",
      overflow: "hidden",
    }}>
      {/* Google Font */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        input::placeholder { color: rgba(255,255,255,0.22) !important; }
        input:focus { border-color: rgba(59,130,246,0.6) !important; background: rgba(59,130,246,0.07) !important; }
        .ggl-btn:hover { background: rgba(255,255,255,0.12) !important; }
        .submit-btn:hover { opacity: 0.9; transform: translateY(-1px); }
        .submit-btn:active { transform: scale(0.98); }
        .switch-link { color: #60a5fa; text-decoration: none; font-weight: 600; cursor: pointer; background: none; border: none; font-family: inherit; font-size: inherit; padding: 0; }
        .switch-link:hover { color: #93c5fd; }
        @media (max-width: 1023px) { .left-panel { display: none !important; } }
      `}</style>

      {/* ── Left Panel: Feature Showcase ── */}
      <aside
        className="left-panel"
        style={{
          flex: 3,
          position: "relative",
          overflow: "hidden",
          padding: "48px 52px",
          display: "flex",
          flexDirection: "column",
          zIndex: 1,
        }}
      >
        <FeatureShowcase />
      </aside>

      {/* ── Divider ── */}
      <div style={{
        position: "relative",
        zIndex: 3,
        display: "flex",
        alignItems: "stretch",
        padding: "0 2px",
      }}>
        <div style={{
          width: 1,
          background: "linear-gradient(to bottom, transparent 5%, rgba(255,255,255,0.08) 30%, rgba(255,255,255,0.08) 70%, transparent 95%)",
        }} />
      </div>

      {/* ── Right Panel: Form ── */}
      <main style={{
        flex: 2,
        position: "relative",
        zIndex: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "48px 44px",
      }}>
        {/* Glass card */}
        <div style={{
          width: "100%",
          maxWidth: 380,
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 20,
          padding: "36px 32px",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04) inset",
        }}>

          {/* Mobile logo */}
          <div className="left-panel" style={{ display: "none" }} />
          <div style={{ display: "none" }} className="mobile-logo" />

          {showForgot ? (
            <>
              {forgotStep === "email" && (
                <>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "rgba(255,255,255,0.3)", marginBottom: 6 }}>
                    Esqueci minha senha
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: "#fff", letterSpacing: "-0.5px", marginBottom: 4, lineHeight: 1.2 }}>
                    Redefinir senha
                  </div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.38)", marginBottom: 26, lineHeight: 1.5 }}>
                    Digite o e-mail da sua conta. Vamos mandar um código de 6 dígitos pra você redefinir a senha.
                  </div>

                  <form onSubmit={submitForgotEmail}>
                    <div style={{ marginBottom: 18 }}>
                      <label htmlFor="forgot-email" style={{ display: "block", fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.45)", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 6 }}>
                        E-mail
                      </label>
                      <input
                        id="forgot-email"
                        type="email"
                        required
                        autoFocus
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="voce@email.com"
                        style={inputStyle}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={busy}
                      className="submit-btn"
                      style={{
                        width: "100%", height: 44, borderRadius: 10,
                        background: "linear-gradient(135deg, #2563eb, #3b82f6)",
                        border: "none", color: "#fff",
                        fontSize: 14, fontWeight: 600,
                        cursor: busy ? "not-allowed" : "pointer",
                        fontFamily: "'Space Grotesk', sans-serif",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                        boxShadow: "0 8px 24px rgba(37,99,235,0.35)",
                        opacity: busy ? 0.7 : 1,
                      }}
                    >
                      {busy ? "Enviando..." : "Enviar código"}
                      <ArrowRight size={16} />
                    </button>
                  </form>

                  <div style={{ textAlign: "center", fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 18 }}>
                    <button type="button" onClick={closeForgotFlow} className="switch-link">
                      ← Voltar pro login
                    </button>
                  </div>
                </>
              )}

              {forgotStep === "code" && (
                <>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "rgba(255,255,255,0.3)", marginBottom: 6 }}>
                    Esqueci minha senha
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: "#fff", letterSpacing: "-0.5px", marginBottom: 4, lineHeight: 1.2 }}>
                    Digite o código
                  </div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.38)", marginBottom: 26, lineHeight: 1.5 }}>
                    Mandamos um código de 6 dígitos pra <strong style={{ color: "rgba(255,255,255,0.6)" }}>{forgotEmail}</strong>.
                  </div>

                  <form onSubmit={submitForgotCode}>
                    <div style={{ marginBottom: 18 }}>
                      <label htmlFor="forgot-codigo" style={{ display: "block", fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.45)", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 6 }}>
                        Código de verificação
                      </label>
                      <input
                        id="forgot-codigo"
                        inputMode="numeric"
                        maxLength={6}
                        autoFocus
                        value={forgotCodigo}
                        onChange={(e) => setForgotCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="000000"
                        style={{ ...inputStyle, textAlign: "center", fontSize: 22, letterSpacing: 8, fontFamily: "ui-monospace, monospace" }}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={forgotCodigo.length !== 6}
                      className="submit-btn"
                      style={{
                        width: "100%", height: 44, borderRadius: 10,
                        background: "linear-gradient(135deg, #2563eb, #3b82f6)",
                        border: "none", color: "#fff",
                        fontSize: 14, fontWeight: 600,
                        cursor: forgotCodigo.length !== 6 ? "not-allowed" : "pointer",
                        fontFamily: "'Space Grotesk', sans-serif",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                        boxShadow: "0 8px 24px rgba(37,99,235,0.35)",
                        opacity: forgotCodigo.length !== 6 ? 0.6 : 1,
                      }}
                    >
                      Continuar
                      <ArrowRight size={16} />
                    </button>
                  </form>

                  <div style={{ textAlign: "center", fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 18 }}>
                    Não recebeu?{" "}
                    <button
                      type="button"
                      onClick={handleResendForgotCode}
                      disabled={resendCooldown > 0 || busy}
                      className="switch-link"
                      style={{ opacity: resendCooldown > 0 ? 0.5 : 1, cursor: resendCooldown > 0 ? "not-allowed" : "pointer" }}
                    >
                      {resendCooldown > 0 ? `Reenviar em ${resendCooldown}s` : "Reenviar código"}
                    </button>
                  </div>
                  <div style={{ textAlign: "center", fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 8 }}>
                    <button type="button" onClick={() => setForgotStep("email")} className="switch-link">
                      ← Voltar
                    </button>
                  </div>
                </>
              )}

              {forgotStep === "newpass" && (
                <>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "rgba(255,255,255,0.3)", marginBottom: 6 }}>
                    Esqueci minha senha
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: "#fff", letterSpacing: "-0.5px", marginBottom: 4, lineHeight: 1.2 }}>
                    Nova senha
                  </div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.38)", marginBottom: 26, lineHeight: 1.5 }}>
                    Escolha uma nova senha pra sua conta.
                  </div>

                  <form onSubmit={submitNewPassword}>
                    <div style={{ marginBottom: 14 }}>
                      <label htmlFor="new-password" style={{ display: "block", fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.45)", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 6 }}>
                        Nova senha
                      </label>
                      <div style={{ position: "relative" }}>
                        <input
                          id="new-password"
                          type={showNewPassword ? "text" : "password"}
                          required
                          autoFocus
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Mínimo 6 caracteres"
                          style={passwordInputStyle}
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword((v) => !v)}
                          style={eyeToggleStyle}
                          aria-label={showNewPassword ? "Ocultar senha" : "Mostrar senha"}
                          tabIndex={-1}
                        >
                          {showNewPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                        </button>
                      </div>
                    </div>
                    <div style={{ marginBottom: 18 }}>
                      <label htmlFor="confirm-password" style={{ display: "block", fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.45)", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 6 }}>
                        Confirmar nova senha
                      </label>
                      <div style={{ position: "relative" }}>
                        <input
                          id="confirm-password"
                          type={showConfirmPassword ? "text" : "password"}
                          required
                          value={confirmNewPassword}
                          onChange={(e) => setConfirmNewPassword(e.target.value)}
                          placeholder="Repita a senha"
                          style={passwordInputStyle}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword((v) => !v)}
                          style={eyeToggleStyle}
                          aria-label={showConfirmPassword ? "Ocultar senha" : "Mostrar senha"}
                          tabIndex={-1}
                        >
                          {showConfirmPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                        </button>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={busy}
                      className="submit-btn"
                      style={{
                        width: "100%", height: 44, borderRadius: 10,
                        background: "linear-gradient(135deg, #2563eb, #3b82f6)",
                        border: "none", color: "#fff",
                        fontSize: 14, fontWeight: 600,
                        cursor: busy ? "not-allowed" : "pointer",
                        fontFamily: "'Space Grotesk', sans-serif",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                        boxShadow: "0 8px 24px rgba(37,99,235,0.35)",
                        opacity: busy ? 0.7 : 1,
                      }}
                    >
                      {busy ? "Salvando..." : "Salvar nova senha"}
                      <ArrowRight size={16} />
                    </button>
                  </form>

                  <div style={{ textAlign: "center", fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 18 }}>
                    <button type="button" onClick={() => setForgotStep("code")} className="switch-link">
                      ← Voltar
                    </button>
                  </div>
                </>
              )}
            </>
          ) : verifyEmailPending ? (
            <>
              {/* Header — tela de verificação de código */}
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "rgba(255,255,255,0.3)", marginBottom: 6 }}>
                Confirme seu e-mail
              </div>
              <div style={{ fontSize: 26, fontWeight: 700, color: "#fff", letterSpacing: "-0.5px", marginBottom: 4, lineHeight: 1.2 }}>
                Digite o código
              </div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.38)", marginBottom: 26, lineHeight: 1.5 }}>
                Mandamos um código de 6 dígitos pra <strong style={{ color: "rgba(255,255,255,0.6)" }}>{verifyEmailPending}</strong>. Confirma aí pra ativar sua conta.
              </div>

              <form onSubmit={submitCodigo}>
                <div style={{ marginBottom: 18 }}>
                  <label htmlFor="codigo" style={{ display: "block", fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.45)", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 6 }}>
                    Código de verificação
                  </label>
                  <input
                    id="codigo"
                    inputMode="numeric"
                    maxLength={6}
                    autoFocus
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    style={{ ...inputStyle, textAlign: "center", fontSize: 22, letterSpacing: 8, fontFamily: "ui-monospace, monospace" }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={busy || codigo.length !== 6}
                  className="submit-btn"
                  style={{
                    width: "100%", height: 44, borderRadius: 10,
                    background: "linear-gradient(135deg, #2563eb, #3b82f6)",
                    border: "none", color: "#fff",
                    fontSize: 14, fontWeight: 600,
                    cursor: (busy || codigo.length !== 6) ? "not-allowed" : "pointer",
                    fontFamily: "'Space Grotesk', sans-serif",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    boxShadow: "0 8px 24px rgba(37,99,235,0.35)",
                    opacity: (busy || codigo.length !== 6) ? 0.6 : 1,
                  }}
                >
                  {busy ? "Confirmando..." : "Confirmar e entrar"}
                  <ArrowRight size={16} />
                </button>
              </form>

              <div style={{ textAlign: "center", fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 18 }}>
                Não recebeu?{" "}
                <button
                  type="button"
                  onClick={handleResendCodigo}
                  disabled={resendCooldown > 0 || busy}
                  className="switch-link"
                  style={{ opacity: resendCooldown > 0 ? 0.5 : 1, cursor: resendCooldown > 0 ? "not-allowed" : "pointer" }}
                >
                  {resendCooldown > 0 ? `Reenviar em ${resendCooldown}s` : "Reenviar código"}
                </button>
              </div>
              <div style={{ textAlign: "center", fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 8 }}>
                <button type="button" onClick={() => { setVerifyEmailPending(""); setCodigo(""); }} className="switch-link">
                  ← Voltar
                </button>
              </div>
            </>
          ) : (
          <>
          {/* Header */}
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "rgba(255,255,255,0.3)", marginBottom: 6 }}>
            {mode === "login" ? "Acesso" : "Cadastro"}
          </div>
          <div style={{ fontSize: 26, fontWeight: 700, color: "#fff", letterSpacing: "-0.5px", marginBottom: 4, lineHeight: 1.2 }}>
            {mode === "login" ? "Entrar na sua conta" : "Criar nova conta"}
          </div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.38)", marginBottom: 26, lineHeight: 1.5 }}>
            {mode === "login"
              ? "Acesse o painel de automação de divulgações"
              : "Comece a divulgar em grupos em poucos minutos"}
          </div>

          {/* Referral badge */}
          {(refCode || (mode === "register" && sessionStorage.getItem("autopost_ref"))) && (
            <div style={{ marginBottom: 20, background: "rgba(59,130,246,0.12)", border: "1px solid rgba(59,130,246,0.25)", borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
              <Gift size={16} style={{ color: "#60a5fa", flexShrink: 0 }} />
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }}>
                Você foi indicado pelo código{" "}
                <strong style={{ color: "#60a5fa", fontFamily: "ui-monospace, monospace" }}>{refCode || sessionStorage.getItem("autopost_ref")}</strong>.
              </div>
            </div>
          )}

          {/* Google Button */}
          {GOOGLE_CLIENT_ID && (
            <div style={{ marginBottom: 20 }}>
              {isCapacitor() ? (
                <button
                  type="button"
                  onClick={handleGoogleNative}
                  disabled={busy}
                  className="ggl-btn"
                  style={{
                    width: "100%", height: 42,
                    background: "rgba(255,255,255,0.07)",
                    border: "1px solid rgba(255,255,255,0.14)",
                    borderRadius: 10,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    color: "rgba(255,255,255,0.85)", fontSize: 13, fontWeight: 500,
                    cursor: "pointer", transition: "background 0.2s",
                    fontFamily: "'Space Grotesk', sans-serif",
                    opacity: busy ? 0.6 : 1,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18">
                    <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
                    <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
                    <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"/>
                    <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.31z"/>
                  </svg>
                  {busy ? "Entrando..." : "Continuar com Google"}
                </button>
              ) : (
                <div id="google-btn" style={{ width: "100%" }} />
              )}

              <p style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", textAlign: "center", lineHeight: 1.4, marginTop: 10 }}>
                Ao continuar com o Google, você concorda com os{" "}
                <a href="/termos.html" target="_blank" rel="noopener noreferrer" style={{ color: "#60a5fa", textDecoration: "underline" }}>
                  Termos de Uso
                </a>{" "}
                e a{" "}
                <a href="/privacidade.html" target="_blank" rel="noopener noreferrer" style={{ color: "#60a5fa", textDecoration: "underline" }}>
                  Política de Privacidade
                </a>
                .
              </p>

              {/* Separator */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "20px 0" }}>
                <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
                <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase", color: "rgba(255,255,255,0.25)" }}>ou continue com e-mail</span>
                <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={submit} data-testid="auth-form">
            {mode === "register" && (
              <div style={{ marginBottom: 14 }}>
                <label htmlFor="name" style={{ display: "block", fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.45)", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 6 }}>
                  Nome completo
                </label>
                <input
                  id="name"
                  data-testid="register-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Seu nome"
                  style={inputStyle}
                />
              </div>
            )}
            <div style={{ marginBottom: 14 }}>
              <label htmlFor="email" style={{ display: "block", fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.45)", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 6 }}>
                E-mail
              </label>
              <input
                id="email"
                type="email"
                required
                data-testid="auth-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="voce@email.com"
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: 6 }}>
              <label htmlFor="password" style={{ display: "block", fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.45)", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 6 }}>
                Senha
              </label>
              <div style={{ position: "relative" }}>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  data-testid="auth-password-input"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  style={passwordInputStyle}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  style={eyeToggleStyle}
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            {mode === "login" && (
              <div style={{ textAlign: "right", marginBottom: 14 }}>
                <button
                  type="button"
                  onClick={() => { setShowForgot(true); setForgotEmail(form.email); }}
                  className="switch-link"
                  style={{ fontSize: 12 }}
                >
                  Esqueci minha senha
                </button>
              </div>
            )}

            {mode === "register" && (
              <label
                style={{
                  display: "flex", alignItems: "flex-start", gap: 8,
                  fontSize: 12, color: "rgba(255,255,255,0.55)",
                  marginBottom: 18, cursor: "pointer", lineHeight: 1.5,
                }}
              >
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  data-testid="auth-accept-terms-checkbox"
                  style={{ marginTop: 2, flexShrink: 0, width: 15, height: 15, cursor: "pointer" }}
                />
                <span>
                  Li e concordo com os{" "}
                  <a href="/termos.html" target="_blank" rel="noopener noreferrer" style={{ color: "#60a5fa", textDecoration: "underline" }}>
                    Termos de Uso
                  </a>{" "}
                  e a{" "}
                  <a href="/privacidade.html" target="_blank" rel="noopener noreferrer" style={{ color: "#60a5fa", textDecoration: "underline" }}>
                    Política de Privacidade
                  </a>
                </span>
              </label>
            )}

            <button
              type="submit"
              disabled={busy || (mode === "register" && !acceptedTerms)}
              data-testid="auth-submit-button"
              className="submit-btn"
              style={{
                width: "100%", height: 44, borderRadius: 10,
                background: "linear-gradient(135deg, #2563eb, #3b82f6)",
                border: "none", color: "#fff",
                fontSize: 14, fontWeight: 600,
                cursor: busy ? "not-allowed" : "pointer",
                transition: "opacity 0.2s, transform 0.15s",
                fontFamily: "'Space Grotesk', sans-serif",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                marginTop: 6,
                boxShadow: "0 8px 24px rgba(37,99,235,0.35)",
                opacity: busy ? 0.7 : 1,
              }}
            >
              {busy ? "Processando..." : mode === "login" ? "Entrar" : "Criar conta"}
              <ArrowRight size={16} />
            </button>
          </form>

          {/* Switch mode */}
          <div style={{ textAlign: "center", fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 18 }}>
            {mode === "login" ? (
              <>
                Não tem conta?{" "}
                <button type="button" onClick={() => setMode("register")} data-testid="switch-to-register" className="switch-link">
                  Criar agora
                </button>
              </>
            ) : (
              <>
                Já tem conta?{" "}
                <button type="button" onClick={() => setMode("login")} data-testid="switch-to-login" className="switch-link">
                  Fazer login
                </button>
              </>
            )}
          </div>
          </>
          )}

          {/* Conheça o novo CRM */}
          <button
            type="button"
            onClick={() => navigate("/crm")}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              width: "100%", marginTop: 20, padding: "11px 16px", borderRadius: 12,
              background: "rgba(168,85,247,0.1)", border: "1px solid rgba(168,85,247,0.35)",
              color: "#d8b4fe", fontSize: 12.5, fontWeight: 700, cursor: "pointer",
            }}
          >
            🇧🇷 Novo: conheça o CRM do AutoPostBR
          </button>
        </div>
      </main>
    </div>
  );
}
